import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabase';
import { S, T, playSound, fireConfetti, DAILY_CHALLENGES, showToast } from '../utils/appUtils';
import NeevBot, { NeevBotMini, NeevBotLevelBar } from '../components/Engagement/NeevBot';
import NeevCoinsBar from '../components/Engagement/NeevCoinsBar';
import MysteryMission from '../components/Engagement/MysteryMission';
import CityMap from '../components/NeevVerse/CityMap';
import { NeevPetWidget } from '../components/NeevPet/NeevPetWorld';
import PetChar from '../components/NeevPet/PetCharacters';
import { AchievementTicker } from '../components/Social/AchievementFeed';
import { CardStrip } from '../components/KnowledgeCards/KnowledgeCards';

// ── ONBOARDING MODAL ─────────────────────────────────────────
const ONBOARD_SLIDES=[
  {emoji:'🌟',title:'Welcome to NeevStar!',body:'India\'s smartest learning app for Classes 3–8. Practice Olympiad questions, play games, and earn XP every day.'},
  {emoji:'🏆',title:'Olympiad Prep — 600+ Questions',body:'Practice IMO, NSO, IEO, IGKO & ICO questions matched to your class. Prepare like a pro from home!'},
  {emoji:'📊',title:'Compete on the Leaderboard',body:'Earn XP by answering questions. Climb the All India leaderboard and show your city who\'s smartest!'},
  {emoji:'🔥',title:'Build Your Daily Streak!',body:'Log in every day to keep your streak alive. Hit 7 days for a bonus reward — hit 30 days and become a NeevStar Legend! 🏅'},
];


// ── STREAK MILESTONE MODAL ─────────────────────────────────────
// ── STREAK RESCUE MODAL ──────────────────────────────────────
export function StreakRescueModal({ brokenStreak, onAccept, onDecline }) {
  const petType = localStorage.getItem('ns_pet_type') || 'lumie';
  const petName = localStorage.getItem('ns_pet_name') || 'Lucky';
  const petEmoji = '✨';
  const petColor = '#7B5EA7';

  const msgs = {
    lumie:     `Your ${brokenStreak}-day streak faded like a shooting star... but I caught it before it disappeared ✨ I have ONE emergency rescue saved for you. Just this once. Because the stars don't give up on you 🌌`,
    cosmo:     `ALERT: ${brokenStreak}-DAY STREAK INTERRUPTED. But I ran a diagnostic — recovery is possible. I have ONE emergency rescue. Initiating now. Because you are the mission. 🤖`,
    nova:      `The ${brokenStreak}-day record fades... but the moon has watched longer streaks return. I have kept ONE rescue for this moment. The ancient ones approve. 🌙`,
    neevo:     `Your ${brokenStreak}-day streak faded like a shooting star... but I caught it before it disappeared ✨ I have ONE emergency rescue saved for you. Just this once. Because the cosmos never gives up on you 🌌`,
    luna:      `Oh no... your ${brokenStreak}-day streak got smudged. But every great painting has a mistake that makes it better. I saved ONE rescue for you — because creativity never quits 🎨`,
    nova_char: `${brokenStreak} days of discovery — interrupted. But scientists don't abandon experiments. I have ONE emergency rescue ready. Let's get back to exploring 🔭`,
    spark:     `${brokenStreak} DAYS AND THE STREAK BREAKS?! No no no — I have ONE emergency rescue and I'm using it RIGHT NOW. We don't quit. Not us. ⚡`,
    glint:     `Your ${brokenStreak}-day gem collection was interrupted... but I caught it before it shattered. I have ONE rescue saved — the rarest treasure is a second chance 💎`,
    orbi:      `The ${brokenStreak}-day orbit was broken. But even planets correct their course. I have ONE rescue waiting. Recalibrating now 🪐`,
    zuki:      `${brokenStreak} DAYS AND WE STUMBLE?! NOT TODAY. I have ONE emergency rescue and I'M USING IT. Champions fall. Champions GET BACK UP. 🔥`,
    echo:      `Your ${brokenStreak}-day streak fell silent... but I echoed it forward before it was lost. I have ONE rescue saved. Because some voices are too important to fade 🌊`,
    terra:     `Your ${brokenStreak}-day streak wilted... but roots run deeper than we see. I have ONE rescue saved — because nature always finds a way to grow back 🌱`,
  };

  useEffect(()=>{ playSound('pet_sad'); },[]);

  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',zIndex:9998,
      display:'flex',alignItems:'center',justifyContent:'center',padding:'24px',
      fontFamily:"'Nunito',sans-serif"}}>
      <motion.div initial={{scale:0.8,opacity:0}} animate={{scale:1,opacity:1}}
        transition={{type:'spring',stiffness:280,damping:22}}
        style={{background:'white',borderRadius:28,padding:'28px 22px',
          maxWidth:380,width:'100%',textAlign:'center',
          boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>

        {/* Sad pet */}
        <motion.div animate={{y:[0,-6,0]}} transition={{repeat:Infinity,duration:2}}
          style={{fontSize:64,marginBottom:8}}>{petEmoji}</motion.div>

        <h2 style={{fontSize:20,fontWeight:900,color:'white',margin:'0 0 6px'}}>
          😢 Your {brokenStreak}-day streak broke...
        </h2>

        {/* Lucky's message */}
        <div style={{background:'#F8F9FF',borderRadius:16,padding:'14px 16px',
          margin:'12px 0 16px',border:`2px solid ${petColor}44`,textAlign:'left'}}>
          <p style={{fontSize:13,fontWeight:800,color:'#555',margin:'0 0 4px'}}>{petName} says:</p>
          <p style={{fontSize:13,fontWeight:700,color:'#333',margin:0,lineHeight:1.6,fontStyle:'italic'}}>
            "{msgs[petType] || msgs.lumie}"
          </p>
        </div>

        {/* Rescue badge */}
        <div style={{background:`linear-gradient(135deg,${petColor},${petColor}BB)`,
          borderRadius:12,padding:'10px 16px',marginBottom:20,display:'inline-block'}}>
          <p style={{fontSize:12,fontWeight:900,color:'white',margin:0}}>
            🛡️ EMERGENCY RESCUE AVAILABLE · 1 per month
          </p>
        </div>

        <motion.button whileTap={{scale:0.97}} onClick={onAccept}
          style={{width:'100%',padding:'15px',borderRadius:16,border:'none',
            background:`linear-gradient(135deg,${petColor},#1A1A6E)`,
            color:'white',fontSize:16,fontWeight:900,cursor:'pointer',
            fontFamily:"'Nunito',sans-serif",marginBottom:10,
            boxShadow:`0 4px 20px ${petColor}66`}}>
          🔥 Restore My {brokenStreak}-Day Streak!
        </motion.button>

        <button onClick={onDecline}
          style={{background:'none',border:'none',color:'#AAA',fontWeight:700,
            fontSize:13,cursor:'pointer',fontFamily:"'Nunito',sans-serif"}}>
          No thanks, start fresh
        </button>
      </motion.div>
    </div>
  );
}

export function StreakMilestoneModal({streak,onClose}){
  const milestone=streak>=30?{days:30,title:'LEGEND! 🏅',sub:'30 days straight!',emoji:'👑',color:'#FFD600',bg:'linear-gradient(135deg,#FF6B00,#FFD600)',bonus:100}:
    streak>=14?{days:14,title:'2-WEEK WARRIOR! 💪',sub:'14 days in a row!',emoji:'🔥',color:'#FF2D55',bg:'linear-gradient(135deg,#FF2D55,#FF6B6B)',bonus:50}:
    streak>=7?{days:7,title:'WEEK CHAMPION! ⚡',sub:'7 days streak!',emoji:'⚡',color:'#8B2BE2',bg:'linear-gradient(135deg,#8B2BE2,#B44FFF)',bonus:30}:
    {days:3,title:'ON FIRE! 🔥',sub:'3 days streak!',emoji:'🔥',color:'#FF6B00',bg:'linear-gradient(135deg,#FF6B00,#FF9500)',bonus:15};
  useEffect(()=>{playSound('levelup');setTimeout(fireConfetti,200);},[]);
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9998,display:'flex',alignItems:'center',justifyContent:'center',padding:'24px'}}>
      <motion.div initial={{scale:0.5,opacity:0}} animate={{scale:1,opacity:1}} transition={{type:'spring',stiffness:300,damping:20}}
        style={{background:'white',borderRadius:'28px',padding:'24px 20px',maxWidth:'90vw',width:'100%',textAlign:'center',
          boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
        <style>{`@keyframes streak-spin{0%{transform:scale(0.5) rotate(-20deg);opacity:0}70%{transform:scale(1.2) rotate(5deg)}100%{transform:scale(1) rotate(0);opacity:1}}.streak-pop{animation:streak-spin 0.6s cubic-bezier(.34,1.56,.64,1) forwards;}`}</style>
        <div className="streak-pop" style={{fontSize:80,marginBottom:8}}>{milestone.emoji}</div>
        <div style={{display:'inline-block',background:milestone.bg,borderRadius:20,padding:'6px 18px',marginBottom:12}}>
          <span style={{fontSize:13,fontWeight:900,color:'#1A1A2E'}}>{milestone.days} DAY STREAK</span>
        </div>
        <h2 style={{fontSize:24,fontWeight:900,color:'white',margin:'0 0 6px'}}>{milestone.title}</h2>
        <p style={{fontSize:15,color:'#666',fontWeight:700,margin:'0 0 20px'}}>{milestone.sub}</p>
        <div style={{background:'#FFF3E0',borderRadius:16,padding:16,marginBottom:20,border:`2px solid ${milestone.color}`}}>
          <p style={{fontSize:22,fontWeight:900,color:milestone.color,margin:'0 0 4px'}}>+{milestone.bonus} Bonus XP! 🎁</p>
          <p style={{fontSize:12,color:'#888',fontWeight:700,margin:0}}>Keep going — next milestone at {milestone.days===3?7:milestone.days===7?14:milestone.days===14?30:'∞'} days!</p>
        </div>
        <motion.button whileTap={{scale:0.97}} onClick={onClose}
          style={{width:'100%',padding:16,borderRadius:16,border:'none',
            background:milestone.bg,color:'white',fontSize:16,fontWeight:900,cursor:'pointer',
            fontFamily:"'Nunito',sans-serif"}}>
          Let's Keep Going! 🚀
        </motion.button>
      </motion.div>
    </div>
  );
}

// ── PARENT REPORT SCREEN ────────────────────────────────────────
export function ParentReportScreen({onBack,profile,xp,streak,completedCounts,coins}){
  const [copied,setCopied]=useState(false);
  const [pinInput,setPinInput]=useState('');
  const [unlocked,setUnlocked]=useState(false);
  const [pinError,setPinError]=useState('');
  const [recentAttempts,setRecentAttempts]=useState([]);
  const [attemptsLoading,setAttemptsLoading]=useState(true);
  const [luckyMsg,setLuckyMsg]=useState(null);
  const [luckyLoading,setLuckyLoading]=useState(false);
  const [dbPin,setDbPin]=useState(null); // PIN from Supabase
  const [pinLoading,setPinLoading]=useState(true);

  // Load PIN from Supabase on mount (falls back to localStorage)
  useEffect(()=>{
    if(!profile?.parent_email){setPinLoading(false);return;}
    supabase.from('bm_child_profiles').select('parent_pin').eq('parent_email',profile.parent_email).single()
      .then(({data})=>{
        if(data?.parent_pin){
          setDbPin(data.parent_pin);
          // Sync to localStorage as fast cache
          localStorage.setItem(`ns_parent_pin_${profile.parent_email}`,data.parent_pin);
        } else {
          // Migrate existing localStorage PIN to DB
          const local=localStorage.getItem(`ns_parent_pin_${profile.parent_email}`);
          if(local){
            setDbPin(local);
            supabase.from('bm_child_profiles').update({parent_pin:local}).eq('parent_email',profile.parent_email).catch(()=>{});
          }
        }
        setPinLoading(false);
      }).catch(()=>{
        // Fallback to localStorage
        const local=localStorage.getItem(`ns_parent_pin_${profile?.parent_email}`);
        setDbPin(local||null);
        setPinLoading(false);
      });
  },[profile?.parent_email]);

  const savedPin=dbPin||localStorage.getItem(`ns_parent_pin_${profile?.parent_email}`);

  const level=Math.floor(xp/200)+1;
  const levelNames=['Curious Cub','Smart Starter','Market Explorer','Deal Maker','Brand Builder',
    'Team Leader','Digital Pioneer','Impact Maker','Scale Champion','Young CEO'];
  const totalCompleted=Object.values(completedCounts||{}).reduce((a,b)=>a+b,0);
  const topSubject=Object.entries(completedCounts||{}).sort((a,b)=>b[1]-a[1])[0];

  const modules=[
    {key:'olympiad',label:'Olympiad Prep',emoji:'🏆'},
    {key:'quiz',label:'Brain Quiz',emoji:'🧠'},
    {key:'maths',label:'Maths',emoji:'➗'},
    {key:'science',label:'Science',emoji:'🔬'},
    {key:'stem',label:'STEM Lab',emoji:'🧪'},
    {key:'biz',label:'Biz World',emoji:'🏪'},
    {key:'money',label:'Money School',emoji:'💰'},
    {key:'language',label:'Language',emoji:'🗣️'},
    {key:'stories',label:'Hall of Legends',emoji:'⭐'},
    {key:'news',label:'News Tower',emoji:'📰'},
    {key:'challenges',label:'Idea Lab',emoji:'💡'},
  ];

  // Load recent CBSE attempts + Lucky summary when unlocked
  useEffect(()=>{
    if(!unlocked||!profile?.parent_email) return;
    supabase.from('ns_cbse_attempts').select('subject,chapter,score,total,attempted_at')
      .eq('student_email',profile.parent_email)
      .order('attempted_at',{ascending:false}).limit(8)
      .then(({data})=>{setRecentAttempts(data||[]);setAttemptsLoading(false);})
      .catch(()=>setAttemptsLoading(false));

    // Load Lucky's memory + generate parent summary
    setLuckyLoading(true);
    supabase.from('ns_pet_memory').select('last_summary,last_topics,last_seen,total_chats,last_struggle')
      .eq('parent_email',profile.parent_email).single()
      .then(async({data:mem})=>{
        if(!mem){ setLuckyLoading(false); return; }
        const petName=localStorage.getItem('ns_pet_name')||'Lucky';
        const petType=localStorage.getItem('ns_pet_type')||'lumie';
        const petEmoji='✨';
        const child=profile.child_name||'your child';
        const daysSince=mem.last_seen?Math.floor((Date.now()-new Date(mem.last_seen))/86400000):null;

        // Build Lucky's message for parents
        const parts=[];
        if(daysSince===0) parts.push(`${child} visited today`);
        else if(daysSince===1) parts.push(`${child} was here yesterday`);
        else if(daysSince>1) parts.push(`${child} hasn't visited in ${daysSince} days — a gentle nudge might help 💛`);

        if(mem.last_summary) parts.push(mem.last_summary);
        if(mem.last_struggle) parts.push(`${child} has been finding ${mem.last_struggle} challenging lately`);
        if(mem.total_chats>0) parts.push(`We've had ${mem.total_chats} conversations so far`);
        if(mem.last_topics?.length) parts.push(`Recent topics: ${mem.last_topics.slice(0,3).join(', ')}`);

        setLuckyMsg({ petName, petEmoji, text: parts.join('. ')+'.' });
        setLuckyLoading(false);
      }).catch(()=>setLuckyLoading(false));
  },[unlocked,profile?.parent_email]);

  const handlePin=()=>{
    if(!savedPin){
      if(pinInput.length!==4||!/^\d{4}$/.test(pinInput)){setPinError('Enter a 4-digit PIN');return;}
      // Save to both localStorage and Supabase
      localStorage.setItem(`ns_parent_pin_${profile?.parent_email}`,pinInput);
      setDbPin(pinInput);
      if(profile?.parent_email){
        supabase.from('bm_child_profiles').update({parent_pin:pinInput}).eq('parent_email',profile.parent_email).catch(()=>{});
      }
      setUnlocked(true);setPinError('');
    } else {
      if(pinInput===savedPin){setUnlocked(true);setPinError('');}
      else{setPinError('Wrong PIN. Try again.');setPinInput('');}
    }
  };

  // Include pet and card stats in parent report
  const petName = localStorage.getItem('ns_pet_name') || null;
  const petType = localStorage.getItem('ns_pet_type') || null;
  const petEmojis = { lumie:'⭐', cosmo:'🤖', nova:'🌙', neevo:'✨', luna:'🎨', nova_char:'🔭', spark:'⚡', glint:'💎', orbi:'🪐', zuki:'🔥', echo:'🌊', terra:'🌿' };
  const petEmoji = petEmojis[petType] || '🐾';
  
  let cardStats = { total:0, counts:{common:0,rare:0,epic:0,legendary:0,godly:0} };
  try { 
    const cards = JSON.parse(localStorage.getItem('ns_knowledge_cards')||'[]');
    cardStats.total = cards.length;
    cards.forEach(c => { if(cardStats.counts[c.rarity]!==undefined) cardStats.counts[c.rarity]++; });
  } catch(_) {}

  const petLine = petName ? `\n${petEmoji} NeevPet: ${petName} (${petType}) is doing well!` : '';
  const cardsLine = cardStats.total > 0 
    ? `\n🃏 Knowledge Cards: ${cardStats.total} collected (${cardStats.counts.godly > 0 ? `✨ ${cardStats.counts.godly} GODLY, ` : ''}${cardStats.counts.legendary} Legendary, ${cardStats.counts.epic} Epic, ${cardStats.counts.rare} Rare)`
    : '';
    
  const luckyLine = luckyMsg?.text ? `\n\n${luckyMsg.petEmoji} ${luckyMsg.petName} says:\n"${luckyMsg.text}"` : '';
  const shareText=`📊 NeevStar Report\n\n👦 ${profile?.child_name||'My Child'} | Class ${profile?.class_level}\n⭐ ${xp} XP | Level ${level} (${levelNames[Math.min(level-1,9)]})\n🔥 ${streak}-day streak\n🪙 ${coins||0} NeevCoins\n📚 ${totalCompleted} activities completed${petLine}${cardsLine}${luckyLine}\n\n🌟 Powered by NeevStar — India's smartest learning app\n👉 neevstar.com`;

  const share=async()=>{
    if(navigator.share){await navigator.share({title:'NeevStar Progress',text:shareText});}
    else{navigator.clipboard.writeText(shareText).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2500);});}
  };

  // ── PIN GATE ──
  if(pinLoading) return(
    <div style={S.screen}>
      <button style={S.backBtn} onClick={onBack}>← Profile</button>
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'70vh'}}>
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:40,marginBottom:12}}>🔒</div>
          <p style={{fontSize:14,fontWeight:700,color:'#888'}}>Loading dashboard...</p>
        </div>
      </div>
    </div>
  );
  if(!unlocked){
    return(
      <div style={S.screen}>
        <button style={S.backBtn} onClick={onBack}>← Profile</button>
        <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'60px 32px',textAlign:'center',minHeight:'70vh'}}>
          <div style={{fontSize:64,marginBottom:16}}>🔒</div>
          <h2 style={{fontSize:22,fontWeight:900,color:'white',margin:'0 0 8px'}}>Parent Dashboard</h2>
          <p style={{fontSize:14,color:'#888',fontWeight:700,margin:'0 0 28px',lineHeight:1.6}}>
            {savedPin?'Enter your 4-digit PIN to view the report':'Set a 4-digit PIN to protect this report'}
          </p>
          <input value={pinInput} onChange={e=>setPinInput(e.target.value.replace(/\D/g,'').slice(0,4))}
            onKeyDown={e=>e.key==='Enter'&&handlePin()}
            type="password" inputMode="numeric" maxLength={4} autoFocus
            placeholder="• • • •"
            style={{fontSize:28,letterSpacing:12,textAlign:'center',padding:'14px 20px',borderRadius:16,
              border:`2px solid ${pinError?T.red:T.blue}`,outline:'none',width:160,fontFamily:"'Nunito',sans-serif",
              fontWeight:900,marginBottom:10}}/>
          {pinError&&<p style={{color:T.red,fontSize:13,fontWeight:800,margin:'0 0 10px'}}>{pinError}</p>}
          <motion.button whileTap={{scale:0.97}} onClick={handlePin}
            style={{padding:'14px 40px',borderRadius:16,border:'none',background:T.grad1,color:'white',
              fontSize:15,fontWeight:900,cursor:'pointer',fontFamily:"'Nunito',sans-serif"}}>
            {savedPin?'Unlock Report 🔓':'Set PIN & View Report'}
          </motion.button>
          {savedPin&&(
            <button onClick={()=>{
              localStorage.removeItem(`ns_parent_pin_${profile?.parent_email}`);
              setDbPin(null);
              if(profile?.parent_email){
                supabase.from('bm_child_profiles').update({parent_pin:null}).eq('parent_email',profile.parent_email).catch(()=>{});
              }
              showToast('PIN reset. Set a new one.','info');
            }}
              style={{background:'none',border:'none',color:'#CCC',fontSize:12,fontWeight:700,cursor:'pointer',marginTop:14,fontFamily:"'Nunito',sans-serif"}}>
              Forgot PIN? Reset
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── DASHBOARD ──
  const activeModules=modules.filter(m=>(completedCounts?.[m.key]||0)>0);
  const notStarted=modules.filter(m=>(completedCounts?.[m.key]||0)===0);

  return(
    <div style={S.screen}>
      <button style={S.backBtn} onClick={onBack}>← Profile</button>
      <div style={{padding:'0 16px 100px'}}>

        {/* Header */}
        <div style={{background:'linear-gradient(135deg,#1A1A2E,#16213E)',borderRadius:24,padding:24,color:'white',marginBottom:16}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{width:56,height:56,borderRadius:'50%',background:'rgba(255,255,255,0.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:28,flexShrink:0}}>
              {profile?.child_name?.charAt(0)||'⭐'}
            </div>
            <div style={{flex:1}}>
              <h2 style={{fontSize:20,fontWeight:900,margin:'0 0 2px'}}>{profile?.child_name||'Your Child'}</h2>
              <p style={{fontSize:12,opacity:0.75,margin:'0 0 6px',fontWeight:700}}>Class {profile?.class_level} • {profile?.city||'India'}</p>
              <span style={{background:'rgba(255,214,0,0.25)',borderRadius:10,padding:'3px 10px',fontSize:11,fontWeight:900,color:'#FFD600'}}>
                Level {level} — {levelNames[Math.min(level-1,9)]}
              </span>
            </div>
            <button onClick={()=>setUnlocked(false)}
              style={{background:'rgba(255,255,255,0.12)',border:'none',color:'rgba(255,255,255,0.6)',
                borderRadius:10,padding:'6px 10px',fontSize:12,cursor:'pointer',fontFamily:"'Nunito',sans-serif",fontWeight:700}}>
              🔒 Lock
            </button>
          </div>
        </div>

        {/* Stats grid — 4 cards */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:16}}>
          {[
            {label:'Total XP',value:xp,emoji:'⭐',color:'#FF9500',bg:'#FFF8E1'},
            {label:'Day Streak',value:`${streak} days`,emoji:'🔥',color:T.red,bg:'#FFF3E0'},
            {label:'NeevCoins',value:coins||0,emoji:'🪙',color:'#00C851',bg:'#E8FAF0'},
            {label:'Activities',value:totalCompleted,emoji:'📚',color:T.blue,bg:'#EEF4FF'},
          ].map((s,i)=>(
            <div key={i} style={{background:s.bg,borderRadius:16,padding:'14px',border:`1.5px solid ${s.color}22`}}>
              <p style={{margin:'0 0 2px',fontSize:11,fontWeight:900,color:'#AAA',letterSpacing:'0.05em'}}>{s.emoji} {s.label.toUpperCase()}</p>
              <p style={{margin:0,fontSize:24,fontWeight:900,color:s.color}}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Lucky's message to parent */}
        {(luckyLoading||(luckyMsg))&&(
          <div style={{background:'linear-gradient(135deg,#1A0533,#0D1B4B)',borderRadius:18,padding:16,marginBottom:14,border:'1.5px solid rgba(255,214,0,0.3)'}}>
            <p style={{fontSize:11,fontWeight:900,color:'rgba(255,214,0,0.8)',margin:'0 0 8px',letterSpacing:'0.06em'}}>
              {luckyLoading?'⭐ ASKING YOUR PET...':'✉️ MESSAGE FROM '+((luckyMsg?.petName)||'LUCKY').toUpperCase()}
            </p>
            {luckyLoading?(
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                {[0,1,2].map(i=>(
                  <motion.div key={i} animate={{y:[0,-4,0]}} transition={{repeat:Infinity,duration:0.6,delay:i*0.15}}
                    style={{width:6,height:6,borderRadius:'50%',background:'rgba(255,214,0,0.6)'}}/>
                ))}
              </div>
            ):(
              <p style={{fontSize:14,fontWeight:700,color:'rgba(255,255,255,0.9)',margin:0,lineHeight:1.7,fontStyle:'italic'}}>
                "{luckyMsg?.text}"
              </p>
            )}
          </div>
        )}

        {/* Level progress */}
        <div style={{background:'white',borderRadius:18,padding:16,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:14}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
            <span style={{fontSize:13,fontWeight:900,color:'#1A1A2E'}}>🏅 Level Progress</span>
            <span style={{fontSize:12,fontWeight:800,color:'#888'}}>{xp%200}/200 XP to Level {level+1}</span>
          </div>
          <div style={{background:'#F0F0F0',borderRadius:8,height:10,overflow:'hidden'}}>
            <motion.div initial={{width:0}} animate={{width:`${((xp%200)/200)*100}%`}} transition={{duration:1}}
              style={{height:'100%',background:`linear-gradient(90deg,#FF6B00,#FFD600)`,borderRadius:8}}/>
          </div>
        </div>

        {/* Time spent this week */}
        {(()=>{
          const email=profile?.parent_email||'';
          const days=['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
          const today=new Date();
          const weekData=Array.from({length:7},(_,i)=>{
            const d=new Date(today);d.setDate(today.getDate()-6+i);
            const key=`ns_time_${email}_${d.toDateString()}`;
            const mins=Math.round((parseInt(localStorage.getItem(key)||'0',10))/60);
            return{day:days[d.getDay()],mins,isToday:i===6};
          });
          const totalMins=weekData.reduce((a,b)=>a+b.mins,0);
          const maxMins=Math.max(...weekData.map(d=>d.mins),1);
          return(
            <div style={{background:'white',borderRadius:18,padding:16,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:14}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
                <p style={{fontSize:13,fontWeight:900,color:'white',margin:0}}>⏱️ Time Spent This Week</p>
                <span style={{fontSize:12,fontWeight:900,color:T.blue}}>{totalMins} mins total</span>
              </div>
              <div style={{display:'flex',alignItems:'flex-end',gap:6,height:60}}>
                {weekData.map((d,i)=>(
                  <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                    <div style={{width:'100%',background:d.isToday?T.orange:d.mins>0?'#1A1A6E':'#F0F0F0',
                      borderRadius:4,height:d.mins>0?`${Math.max(10,(d.mins/maxMins)*50)}px`:'6px',
                      transition:'height 0.5s ease'}}/>
                    <p style={{margin:0,fontSize:9,fontWeight:900,color:d.isToday?T.orange:'#AAA'}}>{d.day}</p>
                  </div>
                ))}
              </div>
              {totalMins===0&&<p style={{fontSize:12,color:'#CCC',fontWeight:700,margin:'8px 0 0',textAlign:'center'}}>Time tracking starts from today's session</p>}
            </div>
          );
        })()}

        {/* Weak areas */}
        {(()=>{
          const all=[
            {key:'olympiad',label:'Olympiad Prep',emoji:'🏆',target:10},
            {key:'quiz',label:'Brain Quiz',emoji:'🧠',target:10},
            {key:'maths',label:'Maths',emoji:'➗',target:10},
            {key:'science',label:'Science',emoji:'🔬',target:10},
            {key:'money',label:'Money School',emoji:'💰',target:5},
            {key:'language',label:'English',emoji:'📖',target:5},
          ];
          const weak=all.filter(m=>(completedCounts?.[m.key]||0)<3).slice(0,3);
          if(weak.length===0) return null;
          return(
            <div style={{background:'#FFF8E1',borderRadius:18,padding:16,marginBottom:14,border:'1.5px solid #FFD600'}}>
              <p style={{fontSize:12,fontWeight:900,color:'#F57F17',margin:'0 0 8px',letterSpacing:'0.04em'}}>⚠️ AREAS NEEDING MORE PRACTICE</p>
              {weak.map((m,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:10,marginBottom:i<weak.length-1?8:0}}>
                  <span style={{fontSize:18}}>{m.emoji}</span>
                  <div style={{flex:1}}>
                    <p style={{margin:0,fontSize:13,fontWeight:800,color:'#795548'}}>{m.label}</p>
                    <p style={{margin:0,fontSize:11,fontWeight:700,color:'#BCAAA4'}}>{completedCounts?.[m.key]||0} of {m.target} sessions done</p>
                  </div>
                  <div style={{background:'#F0F0F0',borderRadius:6,height:6,width:60,overflow:'hidden'}}>
                    <div style={{height:'100%',background:'#FFD600',borderRadius:6,width:`${Math.min(100,((completedCounts?.[m.key]||0)/m.target)*100)}%`}}/>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Smart insight */}
        <div style={{background:'linear-gradient(135deg,#E3F2FD,#EDE7F6)',borderRadius:18,padding:16,marginBottom:14,border:'1.5px solid #C5CAE9'}}>
          <p style={{fontSize:12,fontWeight:900,color:'#5C6BC0',margin:'0 0 6px',letterSpacing:'0.04em'}}>💡 INSIGHT FOR PARENTS</p>
          <p style={{fontSize:13,fontWeight:700,color:'#1A237E',margin:0,lineHeight:1.6}}>
            {streak>=7?`${profile?.child_name||'Your child'} has built a strong ${streak}-day learning habit! 🔥 That consistency predicts long-term academic success.`
             :streak>=3?`${profile?.child_name||'Your child'} is building a learning routine with a ${streak}-day streak. Encourage one session daily to hit the 7-day milestone!`
             :`Encourage ${profile?.child_name||'your child'} to log in daily — even 10 minutes builds strong habits. The streak system rewards consistency!`}
            {topSubject?` Their strongest area is ${topSubject[0]} with ${topSubject[1]} sessions completed.`:''}
          </p>
        </div>

        {/* Streak milestones */}
        <div style={{background:'white',borderRadius:18,padding:16,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:14}}>
          <p style={{fontSize:13,fontWeight:900,color:'white',margin:'0 0 12px'}}>🔥 Streak Milestones</p>
          <div style={{display:'flex',gap:8}}>
            {[3,7,14,30].map(d=>(
              <div key={d} style={{flex:1,textAlign:'center',background:streak>=d?'#FFF3E0':'#F5F5F5',
                borderRadius:12,padding:'10px 6px',border:streak>=d?`2px solid ${T.orange}`:'2px solid transparent'}}>
                <p style={{margin:'0 0 2px',fontSize:16}}>{streak>=d?'✅':'⏳'}</p>
                <p style={{margin:0,fontSize:11,fontWeight:900,color:streak>=d?T.orange:'#BBB'}}>{d}d</p>
              </div>
            ))}
          </div>
        </div>

        {/* Subjects explored */}
        <div style={{background:'white',borderRadius:18,padding:16,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:14}}>
          <p style={{fontSize:13,fontWeight:900,color:'white',margin:'0 0 12px'}}>📚 Subjects Explored ({activeModules.length}/{modules.length})</p>
          {activeModules.length===0&&(
            <div style={{textAlign:'center',padding:'12px 0'}}>
              <p style={{fontSize:24,margin:'0 0 6px'}}>⭐</p>
              <p style={{fontSize:13,fontWeight:800,color:'#888',margin:'0 0 2px'}}>Your pet is waiting!</p>
              <p style={{fontSize:12,fontWeight:700,color:'#BBB',margin:0}}>Complete your first activity and Lucky will celebrate with you 🎉</p>
            </div>
          )}
          {activeModules.map((m,i)=>{
            const count=completedCounts?.[m.key]||0;
            const bar=Math.min(100,count*10);
            return(
              <div key={i} style={{marginBottom:10}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                  <span style={{fontSize:13,fontWeight:700,color:'#555'}}>{m.emoji} {m.label}</span>
                  <span style={{fontSize:12,fontWeight:900,color:T.green}}>{count} sessions</span>
                </div>
                <div style={{background:'#F0F0F0',borderRadius:6,height:6,overflow:'hidden'}}>
                  <motion.div initial={{width:0}} animate={{width:`${bar}%`}} transition={{duration:0.8,delay:i*0.05}}
                    style={{height:'100%',background:`linear-gradient(90deg,${T.green},#00E676)`,borderRadius:6}}/>
                </div>
              </div>
            );
          })}
          {notStarted.length>0&&(
            <p style={{fontSize:11,color:'#CCC',fontWeight:700,margin:'8px 0 0'}}>
              Not yet started: {notStarted.map(m=>m.label).join(', ')}
            </p>
          )}
        </div>

        {/* Recent CBSE quiz scores */}
        <div style={{background:'white',borderRadius:18,padding:16,boxShadow:'0 2px 12px rgba(0,0,0,0.06)',marginBottom:14}}>
          <p style={{fontSize:13,fontWeight:900,color:'white',margin:'0 0 12px'}}>📝 Recent CBSE Quiz Scores</p>
          {attemptsLoading&&<p style={{fontSize:13,color:'#CCC',fontWeight:700,margin:0}}>Loading…</p>}
          {!attemptsLoading&&recentAttempts.length===0&&(
            <div style={{textAlign:'center',padding:'10px 0'}}>
              <p style={{fontSize:20,margin:'0 0 4px'}}>📖</p>
              <p style={{fontSize:12,fontWeight:700,color:'#BBB',margin:0}}>No attempts yet — your pet says try one! It'll be fun 💛</p>
            </div>
          )}
          {recentAttempts.map((a,i)=>{
            const pct=Math.round((a.score/a.total)*100);
            const date=new Date(a.attempted_at).toLocaleDateString('en-IN',{day:'numeric',month:'short'});
            return(
              <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'9px 0',borderBottom:i<recentAttempts.length-1?'1px solid #F5F5F5':'none'}}>
                <div>
                  <p style={{margin:'0 0 1px',fontSize:13,fontWeight:800,color:'#333'}}>{a.subject} — {a.chapter}</p>
                  <p style={{margin:0,fontSize:11,color:'#AAA',fontWeight:700}}>{date}</p>
                </div>
                <span style={{fontSize:13,fontWeight:900,padding:'4px 10px',borderRadius:10,
                  background:pct>=80?'#E0FAE8':pct>=60?'#FFF8E1':'#FFE8E8',
                  color:pct>=80?T.green:pct>=60?T.orange:T.red}}>
                  {a.score}/{a.total} ({pct}%)
                </span>
              </div>
            );
          })}
        </div>

        {/* Share */}
        <motion.button whileTap={{scale:0.97}} onClick={share}
          style={{width:'100%',padding:16,borderRadius:18,border:'none',
            background:'linear-gradient(135deg,#25D366,#128C7E)',color:'white',
            fontSize:15,fontWeight:900,cursor:'pointer',fontFamily:"'Nunito',sans-serif",
            display:'flex',alignItems:'center',justifyContent:'center',gap:8,
            boxShadow:'0 4px 16px rgba(37,211,102,0.35)',marginBottom:10}}>
          {copied?'✓ Copied!':'📤 Share on WhatsApp'}
        </motion.button>
      </div>
    </div>
  );
}

// ── ALL STARLINGS DATA ─────────────────────────────────────────
export const STARLINGS = {
  neevo: {
    id:'neevo', name:'Neevo', role:'Your Cosmic Buddy', superpower:'DISCOVER',
    desc:'Born from the stars, here to help you learn, grow and shine!',
    color:['#7B5EA7','#4A3880'], glow:'rgba(123,94,167,0.5)',
    voiceId:'XrExE9yKIg1WjnnlVkGX', // Matilda — warm girl
    images:{ idle:'/neevo/Neevo_Smile.png', happy:'/neevo/Neevo_Happy.png', excited:'/neevo/Neevo_excited.png', sad:'/neevo/Neevo_sad.png', talking:'/neevo/Neevo_talk.png' },
    messages:["The stars shine brighter when you learn! ✨","The cosmos is so proud of you! 🌌","Every answer brings you closer to the stars! ⭐","Your curiosity is your superpower! 🚀","I missed you! Ready to explore? 🌟"],
    available: true,
  },
  luna: {
    id:'luna', name:'Luna', role:'Creativity Artist', superpower:'CREATE',
    desc:'The dreamy creator with a big heart and endless imagination!',
    color:['#00BCD4','#0288D1'], glow:'rgba(0,188,212,0.5)',
    voiceId:'jBpfuIE2acCO8z3wKNLl', // Gigi — playful girl
    images:{ idle:'/luna/luna_smile.png', happy:'/luna/luna_idle.png', excited:'/luna/luna_excited.png', sad:'/luna/luna_sad.png', talking:'/luna/luna_talk.png' },
    messages:["Let's create something magical today! 🎨","Your imagination has no limits! ✨","Every idea you have is beautiful! 🌈","I believe in your creativity! 💙","Dream big and make it real! 🚀"],
    available: true,
  },
  nova: {
    id:'nova', name:'Nova', role:'Science Explorer', superpower:'DISCOVER',
    desc:'The curious explorer who loves science and discovery!',
    color:['#9C27B0','#6A1B9A'], glow:'rgba(156,39,176,0.5)',
    voiceId:'XrExE9yKIg1WjnnlVkGX', // Matilda
    images:{ idle:'/nova_char/nova_smile.png', happy:'/nova_char/nova_idle.png', excited:'/nova_char/nova_excited.png', sad:'/nova_char/nova_sad.png', talking:'/nova_char/nova_talk.png' },
    messages:["Science is everywhere — let's explore! 🔭","Every question leads to an amazing answer! 🌟","Curiosity is the key to discovery! 💜","You're a scientist at heart! 🧪","The universe has so much to show us! 🌌"],
    available: true,
  },
  spark: {
    id:'spark', name:'Spark', role:'Math Genius', superpower:'SOLVE',
    desc:'The playful thinker who loves numbers, puzzles and challenges!',
    color:['#FF9500','#FF6B00'], glow:'rgba(255,149,0,0.5)',
    voiceId:'IKne3meq5aSn9XLyUdCD', // Charlie — energetic boy
    images:{ idle:'/spark/spark_smile.png', happy:'/spark/spark_idle.png', excited:'/spark/spark_excited.png', sad:'/spark/spark_sad.png', talking:'/spark/spark_talk.png' },
    messages:["Numbers are puzzles waiting to be solved! ⚡","Every maths problem is a superpower challenge! 🔥","You've got a brilliant brain! 🧠","Let's crack this together! 💡","Maths is magic — and you're the magician! ✨"],
    available: true,
  },
  echo: {
    id:'echo', name:'Echo', role:'Word Wizard', superpower:'EXPRESS',
    desc:'The wise storyteller who loves words, languages and communication!',
    color:['#4CAF50','#2E7D32'], glow:'rgba(76,175,80,0.5)',
    voiceId:'XrExE9yKIg1WjnnlVkGX',
    images:{ idle:'/echo/echo_smile.png', happy:'/echo/echo_happy.png', excited:'/echo/echo_excited.png', sad:'/echo/echo_sad.png', talking:'/echo/echo_talk.png' },
    messages:["Words are the most powerful magic! 📖","Every story you tell changes the world! ✨","Your voice deserves to be heard! 🌟","Let's explore the universe of words! 💚","I love how you express yourself! 🎭"],
    available: true,
  },
  terra: {
    id:'terra', name:'Terra', role:'Nature Guardian', superpower:'CARE',
    desc:'The gentle guardian who loves nature, animals and our planet!',
    color:['#8BC34A','#558B2F'], glow:'rgba(139,195,74,0.5)',
    voiceId:'XrExE9yKIg1WjnnlVkGX',
    images:{ idle:'/terra/terra_smile.png', happy:'/terra/terra_happy.png', excited:'/terra/terra_excited.png', sad:'/terra/terra_sad.png', talking:'/terra/terra_talk.png' },
    messages:["Nature has so much to teach us! 🌿","Every living thing is connected! 🌍","Let's protect our beautiful planet! 🌱","The earth needs curious minds like yours! 🦋","You're a guardian of our world! 💚"],
    available: true,
  },
  zuki: {
    id:'zuki', name:'Zuki', role:'Confidence Cheerleader', superpower:'CHEER',
    desc:'The joyful motivator who brings energy, confidence and positivity!',
    color:['#E91E63','#AD1457'], glow:'rgba(233,30,99,0.5)',
    voiceId:'jBpfuIE2acCO8z3wKNLl',
    images:{ idle:'/zuki/zuki_smile.png', happy:'/zuki/zuki_happy.png', excited:'/zuki/zuki_excited.png', sad:'/zuki/zuki_sad.png', talking:'/zuki/zuki_talk.png' },
    messages:["YOU GOT THIS! I believe in you! 💖","Your energy is absolutely contagious! 🔥","Every step forward is a win! 🎉","Keep going — you're amazing! 🌟","Nobody cheers louder for you than me! 💪"],
    available: true,
  },
  orbi: {
    id:'orbi', name:'Orbi', role:'Planner Pro', superpower:'PLAN',
    desc:'The calm planner who helps you organise, focus and stay on track!',
    color:['#2196F3','#1565C0'], glow:'rgba(33,150,243,0.5)',
    voiceId:'IKne3meq5aSn9XLyUdCD',
    images:{ idle:'/orbi/orbi_smile.png', happy:'/orbi/orbi_happy.png', excited:'/orbi/orbi_excited.png', sad:'/orbi/orbi_sad.png', talking:'/orbi/orbi_talk.png' },
    messages:["Let's make a plan and crush it! 📋","One step at a time — we've got this! 💙","Organisation is your superpower! 🎯","Stay focused — great things take time! ⭐","I'll help you stay on track always! 🚀"],
    available: true,
  },
  glint: {
    id:'glint', name:'Glint', role:'Achievement Star', superpower:'GROW',
    desc:'The little collector who sees the magic in every small achievement!',
    color:['#FFD700','#FF8F00'], glow:'rgba(255,215,0,0.5)',
    voiceId:'jBpfuIE2acCO8z3wKNLl',
    images:{ idle:'/glint/glint_smile.png', happy:'/glint/glint_happy.png', excited:'/glint/glint_excited.png', sad:'/glint/glint_sad.png', talking:'/glint/glint_talk.png' },
    messages:["Every achievement deserves to shine! ✨","You're collecting greatness every day! 🏆","Small wins lead to big victories! ⭐","I keep track of every amazing thing you do! 💛","You are growing more than you know! 🌟"],
    available: true,
  },
};

// ── PREMIUM CHARACTER SELECTOR (reusable) ─────────────────────
export function PremiumCharacterSelector({ profile, onDone, title='Choose your Star Buddy!' }) {
  const [selected, setSelected] = useState(null);
  const [nameInput, setNameInput] = useState('');
  const [step, setStep] = useState('pick'); // 'pick' | 'name'
  const [saving, setSaving] = useState(false);
  const childName = (profile?.child_name || 'Champion').split(' ')[0];
  const availableStarlings = Object.values(STARLINGS).filter(s => s.available);
  const selectedChar = selected ? STARLINGS[selected] : null;

  const handleSave = async () => {
    if (saving || !selected) return;
    setSaving(true);
    const char = STARLINGS[selected];
    const finalName = nameInput.trim() || char.name;
    localStorage.setItem('ns_pet_type', selected);
    localStorage.setItem('ns_pet_name', finalName);
    if (profile?.parent_email) {
      localStorage.setItem(`ns_bot_name_${profile.parent_email}`, finalName);
      await supabase.from('bm_child_profiles')
        .update({ pet_type: selected, pet_name: finalName })
        .eq('parent_email', profile.parent_email);
    }
    playSound('pet_evolve');
    fireConfetti();
    setTimeout(onDone, 1200);
  };

  return (
    <div style={{position:'fixed',inset:0,zIndex:9999,overflowY:'auto',fontFamily:"'Nunito',sans-serif"}}>

      {/* Designer background image */}
      <div style={{
        position:'fixed', inset:0,
        backgroundImage:'url(/neevo/character_intro_bg.png)',
        backgroundSize:'cover',
        backgroundPosition:'center top',
        backgroundRepeat:'no-repeat',
        zIndex:0,
      }}/>
      {/* Dark overlay for readability on mobile */}
      <div style={{position:'fixed',inset:0,background:'rgba(10,5,30,0.45)',zIndex:0}}/>

      <div style={{maxWidth:430,margin:'0 auto',padding:'24px 16px 48px',position:'relative',zIndex:1}}>
        {/* Close button */}
        <button onClick={onDone}
          style={{background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',
            color:'white',borderRadius:12,padding:'6px 14px',fontSize:13,fontWeight:700,
            cursor:'pointer',marginBottom:16,fontFamily:"'Nunito',sans-serif"}}>
          ✕ Close
        </button>

        {step === 'pick' ? (
          <>
            {/* Header */}
            <div style={{textAlign:'center',marginBottom:20}}>
              <motion.p animate={{opacity:[0.5,1,0.5]}} transition={{duration:2,repeat:Infinity}}
                style={{fontSize:11,color:'rgba(255,255,255,0.5)',fontWeight:700,margin:'0 0 4px',letterSpacing:1}}>
                Welcome to
              </motion.p>
              <h1 style={{fontSize:28,fontWeight:900,margin:'0 0 4px',
                background:'linear-gradient(135deg,#FFFFFF,#FFD700)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
                neevstar ✦
              </h1>
              <p style={{fontSize:15,fontWeight:800,color:'#FFD700',margin:'0 0 4px'}}>{title}</p>
              <p style={{fontSize:11,color:'rgba(255,255,255,0.45)',fontWeight:600,margin:0}}>
                Each Starling has a unique superpower to help you learn, grow and shine!
              </p>
            </div>

            {/* Available characters — 3-column grid */}
            <p style={{textAlign:'center',fontSize:10,color:'rgba(255,255,255,0.5)',fontWeight:700,margin:'0 0 12px',letterSpacing:1.5}}>
              ✦ CHOOSE YOUR STARLING ✦
            </p>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:16}}>
              {availableStarlings.map((char,i)=>{
                const isSel = selected === char.id;
                return (
                  <motion.div key={char.id}
                    initial={{opacity:0,scale:0.8}} animate={{opacity:1,scale:1}} transition={{delay:i*0.1,type:'spring'}}
                    whileTap={{scale:0.96}}
                    onClick={()=>setSelected(char.id)}
                    style={{
                      borderRadius:20, padding:'16px 12px', textAlign:'center', cursor:'pointer',
                      background: isSel ? `linear-gradient(135deg,${char.color[0]},${char.color[1]})` : 'rgba(255,255,255,0.08)',
                      border: `2px solid ${isSel ? char.color[0] : 'rgba(255,255,255,0.15)'}`,
                      boxShadow: isSel ? `0 8px 24px ${char.glow}` : 'none',
                      transition:'all 0.2s',position:'relative',
                    }}>
                    {isSel && (
                      <div style={{position:'absolute',top:8,right:8,background:'rgba(255,255,255,0.3)',
                        borderRadius:'50%',width:20,height:20,display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <span style={{fontSize:10,fontWeight:900,color:'white'}}>✓</span>
                      </div>
                    )}
                    {/* Character image with glow */}
                    <motion.div style={{position:'relative',display:'inline-block'}}
                      animate={{y:[0,-8,0]}} transition={{duration:2+i*0.3,repeat:Infinity,ease:'easeInOut'}}>
                      <div style={{position:'absolute',inset:-10,borderRadius:'50%',
                        background:`radial-gradient(circle,${char.glow} 0%,transparent 70%)`,
                        filter:'blur(8px)',pointerEvents:'none'}}/>
                      <img src={isSel ? char.images.excited : char.images.idle}
                        alt={char.name}
                        style={{width:72,height:72,objectFit:'contain',position:'relative',
                          filter:`drop-shadow(0 6px 12px ${char.glow})`}}/>
                    </motion.div>
                    <p style={{margin:'6px 0 2px',fontSize:13,fontWeight:900,color:'white'}}>{char.name}</p>
                    <div style={{background:'rgba(255,255,255,0.2)',borderRadius:6,padding:'2px 6px',display:'inline-block'}}>
                      <span style={{fontSize:8,fontWeight:900,color:'white',letterSpacing:0.5}}>⚡ {char.superpower}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Selected character description */}
            {selectedChar && (
              <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}}
                style={{background:'rgba(255,255,255,0.08)',borderRadius:14,padding:'12px 16px',
                  marginBottom:16,border:'1px solid rgba(255,255,255,0.12)',textAlign:'center'}}>
                <p style={{margin:0,fontSize:13,color:'rgba(255,255,255,0.85)',fontWeight:700,lineHeight:1.5}}>
                  💬 "{selectedChar.messages[0]}"
                </p>
                <p style={{margin:'6px 0 0',fontSize:11,color:'rgba(255,255,255,0.5)',fontWeight:600}}>
                  — {selectedChar.name}, {selectedChar.role}
                </p>
              </motion.div>
            )}

            {/* CTA */}
            <motion.button whileTap={{scale:0.97}}
              onClick={()=>{ if(selected) setStep('name'); }}
              disabled={!selected}
              animate={selected?{boxShadow:['0 0 0 0 rgba(255,215,0,0.4)','0 0 0 12px rgba(255,215,0,0)']}:{}}
              transition={{duration:1.5,repeat:Infinity}}
              style={{width:'100%',padding:'16px',borderRadius:20,border:'none',
                background: selected ? `linear-gradient(135deg,${selectedChar?.color[0]||'#FFD700'},${selectedChar?.color[1]||'#FF9500'})` : 'rgba(255,255,255,0.1)',
                color: selected ? 'white' : 'rgba(255,255,255,0.3)',
                fontSize:17,fontWeight:900,cursor:selected?'pointer':'not-allowed',
                fontFamily:"'Nunito',sans-serif",
                boxShadow: selected ? `0 6px 24px ${selectedChar?.glow||'rgba(255,215,0,0.4)'}` : 'none'}}>
              {selected ? `✦ Choose ${selectedChar?.name}! →` : 'Pick your Star Buddy first ✦'}
            </motion.button>
          </>
        ) : (
          /* Name step */
          <div style={{textAlign:'center',paddingTop:20}}>
            <motion.img
              src={selectedChar?.images.excited || '/neevo/Neevo_excited.png'}
              alt={selectedChar?.name}
              animate={{y:[0,-10,0]}} transition={{repeat:Infinity,duration:2}}
              style={{width:160,height:160,objectFit:'contain',
                filter:`drop-shadow(0 12px 28px ${selectedChar?.glow||'rgba(255,215,0,0.5)'})`,marginBottom:16}}/>
            <h2 style={{fontSize:24,fontWeight:900,color:'white',margin:'0 0 6px'}}>
              Name your {selectedChar?.name}! ✨
            </h2>
            <p style={{fontSize:13,color:'rgba(255,255,255,0.55)',fontWeight:700,margin:'0 0 20px'}}>
              Hey {childName}! Give your buddy a special name!
            </p>
            <input value={nameInput} onChange={e=>setNameInput(e.target.value.slice(0,12))}
              placeholder={selectedChar?.name || 'Name...'}
              style={{width:'100%',padding:'14px 16px',borderRadius:14,
                border:`2px solid ${selectedChar?.color[0]||'rgba(255,215,0,0.4)'}66`,
                background:'rgba(255,255,255,0.08)',outline:'none',
                fontSize:18,fontWeight:900,fontFamily:"'Nunito',sans-serif",
                color:'white',textAlign:'center',boxSizing:'border-box',marginBottom:16}}/>
            <motion.button whileTap={{scale:0.97}} onClick={handleSave} disabled={saving}
              style={{width:'100%',padding:'16px',borderRadius:16,border:'none',
                background:`linear-gradient(135deg,${selectedChar?.color[0]||'#7B5EA7'},${selectedChar?.color[1]||'#4A3880'})`,
                color:'white',fontSize:17,fontWeight:900,cursor:'pointer',
                fontFamily:"'Nunito',sans-serif",
                boxShadow:`0 4px 20px ${selectedChar?.glow||'rgba(123,94,167,0.4)'}`,marginBottom:12}}>
              {saving?'Saving... ✨':`Let's go, ${nameInput||selectedChar?.name}! 🚀`}
            </motion.button>
            <button onClick={()=>setStep('pick')}
              style={{background:'none',border:'none',color:'rgba(255,255,255,0.4)',
                fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:"'Nunito',sans-serif"}}>
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── PET PICKER FOR EXISTING USERS ───────────────────────────
const PICKER_CHARS = [
  { id:'neevo',     name:'Neevo',  role:'Cosmic Buddy',      img:'/neevo/Neevo_Happy.png',         color:'#7B5EA7', accent:'#FFD700' },
  { id:'luna',      name:'Luna',   role:'Creativity Artist', img:'/luna/luna_idle.png',             color:'#00BCD4', accent:'#0288D1' },
  { id:'nova_char', name:'Nova',   role:'Science Explorer',  img:'/nova_char/nova_smile.png',       color:'#9C27B0', accent:'#6A1B9A' },
  { id:'spark',     name:'Spark',  role:'Math Genius',       img:'/spark/spark_smile.png',          color:'#FF9500', accent:'#FF6B00' },
  { id:'glint',     name:'Glint',  role:'Treasure Hunter',   img:'/glint/glint_smile.png',          color:'#F59E0B', accent:'#D97706' },
  { id:'orbi',      name:'Orbi',   role:'Planet Explorer',   img:'/orbi/orbi_smile.png',            color:'#3B82F6', accent:'#1D4ED8' },
  { id:'zuki',      name:'Zuki',   role:'Speed Champion',    img:'/zuki/zuki_smile.png',            color:'#EF4444', accent:'#B91C1C' },
  { id:'echo',      name:'Echo',   role:'Language Wizard',   img:'/echo/echo_smile.png',            color:'#10B981', accent:'#059669' },
  { id:'terra',     name:'Terra',  role:'Nature Guardian',   img:'/terra/terra_smile.png',          color:'#84CC16', accent:'#4D7C0F' },
];

export function ExistingUserPetPicker({profile, onDone}){
  const [selected, setSelected] = useState(PICKER_CHARS[0]);
  const [nameInput, setNameInput] = useState('');
  const [saving, setSaving] = useState(false);
  const childName = (profile?.child_name||'Champion').split(' ')[0];

  const handleSave = async () => {
    if(saving) return;
    setSaving(true);
    const finalName = nameInput.trim() || selected.name;
    localStorage.setItem('ns_pet_type', selected.id);
    localStorage.setItem('ns_pet_name', finalName);
    if(profile?.parent_email){
      localStorage.setItem(`ns_bot_name_${profile.parent_email}`, finalName);
      await supabase.from('bm_child_profiles')
        .update({pet_type: selected.id, pet_name: finalName})
        .eq('parent_email', profile.parent_email);
    }
    playSound('pet_evolve');
    fireConfetti();
    setTimeout(onDone, 1200);
  };

  return(
    <div style={{position:'fixed',inset:0,background:'linear-gradient(135deg,rgba(10,10,40,0.97),rgba(30,10,60,0.97))',zIndex:9999,
      display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Nunito',sans-serif",overflowY:'auto',padding:'16px'}}>
      <motion.div initial={{scale:0.8,opacity:0}} animate={{scale:1,opacity:1}} transition={{type:'spring',stiffness:280,damping:24}}
        style={{background:'linear-gradient(135deg,#1A0A3E,#0D1B4B)',borderRadius:28,padding:'28px 20px',
          width:'100%',maxWidth:420,textAlign:'center',border:'1px solid rgba(123,94,167,0.4)',
          boxShadow:'0 0 60px rgba(123,94,167,0.3)'}}>

        <h2 style={{fontSize:22,fontWeight:900,color:'white',margin:'0 0 4px'}}>Choose Your NeevPet! 🌟</h2>
        <p style={{fontSize:13,color:'rgba(255,255,255,0.5)',fontWeight:700,margin:'0 0 20px'}}>
          Hey {childName}! Pick your learning buddy — they grow with you 🚀
        </p>

        {/* Selected character preview */}
        <motion.div animate={{y:[0,-10,0]}} transition={{repeat:Infinity,duration:2.5,ease:'easeInOut'}}
          style={{position:'relative',display:'inline-block',marginBottom:12}}>
          <div style={{position:'absolute',inset:-20,borderRadius:'50%',
            background:`radial-gradient(circle,${selected.color}55 0%,transparent 70%)`,filter:'blur(12px)'}}/>
          <img src={selected.img} alt={selected.name}
            style={{width:130,height:130,objectFit:'contain',position:'relative',zIndex:1,
              filter:`drop-shadow(0 8px 20px ${selected.color}88)`}}/>
        </motion.div>

        <p style={{margin:'0 0 4px',fontSize:18,fontWeight:900,color:'white'}}>{selected.name}</p>
        <p style={{margin:'0 0 16px',fontSize:12,fontWeight:800,
          background:`${selected.color}33`,color:selected.accent,
          borderRadius:8,padding:'3px 12px',display:'inline-block'}}>{selected.role}</p>

        {/* Character grid */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginBottom:20}}>
          {PICKER_CHARS.map(c=>(
            <motion.div key={c.id} whileTap={{scale:0.93}} onClick={()=>setSelected(c)}
              style={{background:selected.id===c.id?`${c.color}33`:'rgba(255,255,255,0.06)',
                border:`2px solid ${selected.id===c.id?c.accent:'rgba(255,255,255,0.1)'}`,
                borderRadius:14,padding:'10px 6px',cursor:'pointer',transition:'all 0.2s'}}>
              <img src={c.img} alt={c.name} style={{width:44,height:44,objectFit:'contain',display:'block',margin:'0 auto 4px'}}/>
              <p style={{margin:0,fontSize:10,fontWeight:900,color:selected.id===c.id?'white':'rgba(255,255,255,0.5)'}}>{c.name}</p>
            </motion.div>
          ))}
        </div>

        {/* Name input */}
        <div style={{marginBottom:16}}>
          <p style={{fontSize:12,fontWeight:800,color:'rgba(255,255,255,0.5)',margin:'0 0 8px'}}>
            Give {selected.name} a nickname (optional):
          </p>
          <input value={nameInput} onChange={e=>setNameInput(e.target.value.slice(0,12))}
            placeholder={selected.name}
            style={{width:'100%',padding:'13px 16px',borderRadius:14,border:`2px solid ${selected.color}66`,
              background:'rgba(255,255,255,0.08)',outline:'none',fontSize:17,fontWeight:900,
              fontFamily:"'Nunito',sans-serif",color:'white',textAlign:'center',boxSizing:'border-box'}}/>
        </div>

        <motion.button whileTap={{scale:0.97}} onClick={handleSave} disabled={saving}
          style={{width:'100%',padding:'16px',borderRadius:16,border:'none',
            background:`linear-gradient(135deg,${selected.color},${selected.accent})`,
            color:'white',fontSize:17,fontWeight:900,cursor:'pointer',
            fontFamily:"'Nunito',sans-serif",boxShadow:`0 4px 20px ${selected.color}66`}}>
          {saving?`Hatching ${nameInput||selected.name}... ✨`:`Let's go, ${nameInput||selected.name}! 🚀`}
        </motion.button>
      </motion.div>
    </div>
  );
}

export const SUBJECT_OPTIONS=[
  {id:'quiz',emoji:'🧠',label:'General Quiz'},
  {id:'maths',emoji:'➗',label:'Maths'},
  {id:'science',emoji:'🔬',label:'Science'},
  {id:'olympiad',emoji:'🏆',label:'Olympiad Prep'},
  {id:'money',emoji:'💰',label:'Money School'},
  {id:'ai_tutor',emoji:'🤖',label:'AI School'},
  {id:'language',emoji:'📖',label:'English'},
  {id:'stem',emoji:'⚡',label:'STEM'},
];

export function OnboardingModal({onDone,profile,onXPEarned,onNavigate}){
  const [step,setStep]=useState(0);
  const [picked,setPicked]=useState([]);
  const [claimed,setClaimed]=useState(false);
  const [selectedPet,setSelectedPet]=useState(null);
  const [petNameInput,setPetNameInput]=useState('');
  const name=(profile?.child_name||'Explorer').split(' ')[0];

  // Neevo is the one and only companion
  const PETS=[
    { type:'nova', emoji:'✨', name:'Neevo', desc:'Your cosmic learning buddy — born from the stars, here to help you learn, grow and shine! 🌌' },
  ];

  const toggle=(id)=>setPicked(p=>p.includes(id)?p.filter(x=>x!==id):p.length<3?[...p,id]:p);

  const claimAndGo=()=>{
    if(claimed)return;
    setClaimed(true);
    // Save pet choice locally + to Supabase
    if(selectedPet){
      const finalName=petNameInput.trim()||'Neevo';
      localStorage.setItem('ns_pet_type',selectedPet);
      localStorage.setItem('ns_pet_name',finalName);
      if(profile?.parent_email){
        localStorage.setItem(`ns_bot_name_${profile.parent_email}`,finalName);
        supabase.from('bm_child_profiles')
          .update({pet_type:selectedPet,pet_name:finalName})
          .eq('parent_email',profile.parent_email)
          .then(()=>{}).catch(()=>{});
      }
    }
    playSound('levelup');
    fireConfetti();
    if(onXPEarned)onXPEarned(50);
    setTimeout(()=>{
      onDone(picked);
      if(picked.length>0&&onNavigate)onNavigate(picked[0]);
    },1400);
  };

  const canNext=(step===0)||(step===1&&!!selectedPet)||(step===2)||(step===3&&picked.length>0)||step===4;

  const STEPS=[
    // Step 0 — Welcome with Neevo
    <motion.div key="welcome" initial={{opacity:0,scale:0.9}} animate={{opacity:1,scale:1}} style={{textAlign:'center'}}>
      <motion.div animate={{y:[0,-10,0]}} transition={{duration:2,repeat:Infinity}} style={{margin:'0 0 8px',display:'inline-block'}}>
        <img src="/neevo/Neevo_Happy.png" alt="Neevo" style={{width:120,height:120,objectFit:'contain'}}/>
      </motion.div>
      <h2 style={{fontSize:24,fontWeight:900,color:'white',margin:'0 0 8px'}}>Hey {name}! 👋</h2>
      <p style={{fontSize:14,color:'#555',fontWeight:700,lineHeight:1.7,margin:'0 0 18px'}}>
        Welcome to <span style={{color:'#FF6B00',fontWeight:900}}>NeevStar</span>!<br/>
        You're about to meet your <span style={{color:'#0066FF',fontWeight:900}}>AI learning companion</span> — a friend who remembers you, grows with you, and never gives up on you.
      </p>
      <div style={{display:'flex',flexDirection:'column',gap:8,textAlign:'left'}}>
        {[['🧠','Your pet remembers everything you talk about'],['💛','It gets sad when you\'re away, happy when you learn'],['🎭','It tells stories, jokes, and explains anything'],['🌱','It grows and evolves as you earn XP']].map(([icon,text])=>(
          <div key={text} style={{display:'flex',alignItems:'center',gap:10,background:'#F8F9FF',borderRadius:10,padding:'10px 12px'}}>
            <span style={{fontSize:20,flexShrink:0}}>{icon}</span>
            <p style={{margin:0,fontSize:13,fontWeight:700,color:'#444'}}>{text}</p>
          </div>
        ))}
      </div>
    </motion.div>,

    // Step 1 — Premium Character Showcase
    <motion.div key="petpick" initial={{opacity:0}} animate={{opacity:1}} style={{margin:'-28px -20px',borderRadius:'20px 20px 0 0',overflow:'hidden'}}>
      {/* Cosmic background */}
      <div style={{
        backgroundImage:'url(/neevo/character_intro_bg.png)',
        backgroundSize:'cover',
        backgroundPosition:'center top',
        minHeight:'100vh', padding:'24px 16px 32px', position:'relative', overflow:'hidden',
        fontFamily:"'Nunito',sans-serif",
      }}>
        {/* Dark overlay */}
        <div style={{position:'absolute',inset:0,background:'rgba(10,5,30,0.5)',pointerEvents:'none',zIndex:0}}/>

        {/* Header */}
        <div style={{textAlign:'center',marginBottom:20,position:'relative',zIndex:2,marginTop:'55vh'}}>
          <motion.p initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}} transition={{delay:0.2}}
            style={{fontSize:13,color:'rgba(255,255,255,0.6)',fontWeight:700,margin:'0 0 4px',letterSpacing:1}}>
            Welcome to
          </motion.p>
          <motion.h1 initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}} transition={{delay:0.3}}
            style={{fontSize:32,fontWeight:900,margin:'0 0 4px',
              background:'linear-gradient(135deg,#FFFFFF,#FFD700)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>
            neevstar ✦
          </motion.h1>
          <motion.p initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}} transition={{delay:0.4}}
            style={{fontSize:16,fontWeight:800,color:'#FFD700',margin:'0 0 2px'}}>
            Choose your Star Buddy!
          </motion.p>
          <motion.p initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.5}}
            style={{fontSize:11,color:'rgba(255,255,255,0.5)',fontWeight:600,margin:0}}>
            Each Starling has a unique superpower to help you learn, grow and shine!
          </motion.p>
        </div>

        {/* CENTER — Neevo (available) */}
        <motion.div initial={{opacity:0,scale:0.8}} animate={{opacity:1,scale:1}} transition={{delay:0.3,type:'spring'}}
          style={{textAlign:'center',position:'relative',zIndex:3,marginBottom:8}}>
          {/* Glow ring */}
          <motion.div animate={{scale:[1,1.08,1],opacity:[0.4,0.8,0.4]}} transition={{duration:2,repeat:Infinity}}
            style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',
              width:180,height:180,borderRadius:'50%',
              background:'radial-gradient(circle,rgba(255,215,0,0.3) 0%,transparent 70%)',pointerEvents:'none'}}/>
          {/* Platform glow */}
          <div style={{position:'absolute',bottom:8,left:'50%',transform:'translateX(-50%)',
            width:130,height:20,borderRadius:'50%',
            background:'radial-gradient(ellipse,rgba(255,215,0,0.4) 0%,transparent 70%)',filter:'blur(6px)'}}/>
          <motion.img
            src={selectedPet?'/neevo/Neevo_excited.png':'/neevo/Neevo_Happy.png'}
            alt="Neevo"
            animate={{y:[0,-12,0]}}
            transition={{duration:2.5,repeat:Infinity,ease:'easeInOut'}}
            onClick={()=>setSelectedPet('nova')}
            style={{width:140,height:140,objectFit:'contain',cursor:'pointer',position:'relative',zIndex:1,
              filter:'drop-shadow(0 12px 28px rgba(255,215,0,0.5))'}}
          />
          {/* Platform */}
          <div style={{width:120,height:12,borderRadius:'50%',margin:'-8px auto 0',
            background:'linear-gradient(135deg,rgba(255,215,0,0.4),rgba(123,94,167,0.4))',
            filter:'blur(2px)'}}/>
          <motion.div animate={{opacity:[0.7,1,0.7]}} transition={{duration:1.5,repeat:Infinity}}
            style={{marginTop:6}}>
            <p style={{margin:'0 0 2px',fontSize:18,fontWeight:900,color:'#1A1A2E'}}>Neevo</p>
            <p style={{margin:0,fontSize:11,fontWeight:700,color:'#FFD700'}}>Your Cosmic Buddy</p>
          </motion.div>
          {selectedPet&&(
            <motion.div initial={{scale:0}} animate={{scale:1}} transition={{type:'spring'}}
              style={{marginTop:6,background:'linear-gradient(135deg,#FFD700,#FF9500)',
                borderRadius:20,padding:'4px 16px',display:'inline-block'}}>
              <span style={{fontSize:12,fontWeight:900,color:'#1A1A2E'}}>✓ Selected!</span>
            </motion.div>
          )}
        </motion.div>

        {/* Coming Soon Characters Grid */}
        <motion.p initial={{opacity:0}} animate={{opacity:1}} transition={{delay:0.6}}
          style={{textAlign:'center',fontSize:11,color:'rgba(255,255,255,0.4)',
            fontWeight:700,margin:'12px 0 10px',letterSpacing:1}}>
          MORE STARLINGS COMING SOON ✨
        </motion.p>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:20}}>
          {[
            {name:'Nova',role:'Science Explorer',color:'#9C27B0',emoji:'🔭'},
            {name:'Luna',role:'Creativity Artist',color:'#00BCD4',emoji:'🎨'},
            {name:'Spark',role:'Math Genius',color:'#FF9500',emoji:'⚡'},
            {name:'Echo',role:'Word Wizard',color:'#4CAF50',emoji:'📖'},
            {name:'Terra',role:'Nature Guardian',color:'#8BC34A',emoji:'🌿'},
            {name:'Zuki',role:'Cheerleader',color:'#E91E63',emoji:'💖'},
            {name:'Orbi',role:'Planner Pro',color:'#2196F3',emoji:'📋'},
            {name:'Glint',role:'Achievement Star',color:'#FFD700',emoji:'🏆'},
          ].map((c,i)=>(
            <motion.div key={c.name}
              initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.5+i*0.06}}
              style={{background:'rgba(255,255,255,0.06)',borderRadius:14,padding:'10px 6px',
                textAlign:'center',border:'1px solid rgba(255,255,255,0.1)',
                backdropFilter:'blur(8px)',position:'relative',overflow:'hidden'}}>
              {/* Lock */}
              <div style={{position:'absolute',top:4,right:4,fontSize:9,background:'rgba(0,0,0,0.4)',
                borderRadius:8,padding:'1px 4px',color:'rgba(255,255,255,0.5)'}}>🔒</div>
              <div style={{fontSize:28,marginBottom:4,filter:'grayscale(30%)'}}>{c.emoji}</div>
              <p style={{margin:'0 0 1px',fontSize:10,fontWeight:900,color:'#1A1A2E'}}>{c.name}</p>
              <p style={{margin:0,fontSize:8,color:'rgba(255,255,255,0.4)',fontWeight:600,lineHeight:1.3}}>{c.role}</p>
            </motion.div>
          ))}
        </div>

        {/* Features strip */}
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.8}}
          style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:20}}>
          {[
            {icon:'🎮',title:'Learn with Fun',desc:'Exciting lessons & interactive games'},
            {icon:'⭐',title:'Grow Every Day',desc:'Track progress & unlock abilities'},
            {icon:'💖',title:'Be Your Best',desc:'Build confidence & shine bright'},
          ].map(f=>(
            <div key={f.title} style={{background:'rgba(255,255,255,0.07)',borderRadius:12,padding:'10px 8px',
              textAlign:'center',border:'1px solid rgba(255,255,255,0.08)'}}>
              <div style={{fontSize:20,marginBottom:4}}>{f.icon}</div>
              <p style={{margin:'0 0 2px',fontSize:9,fontWeight:900,color:'#1A1A2E'}}>{f.title}</p>
              <p style={{margin:0,fontSize:8,color:'rgba(255,255,255,0.4)',fontWeight:600,lineHeight:1.3}}>{f.desc}</p>
            </div>
          ))}
        </motion.div>

        {/* CTA Button */}
        <motion.button
          whileTap={{scale:0.97}}
          onClick={()=>{ setSelectedPet('nova'); setTimeout(()=>setStep(s=>s+1),300); }}
          animate={selectedPet?{}:{boxShadow:['0 0 0 0 rgba(255,215,0,0.4)','0 0 0 14px rgba(255,215,0,0)']}}
          transition={{duration:1.5,repeat:Infinity}}
          style={{width:'100%',padding:'16px',borderRadius:20,border:'none',
            background:'linear-gradient(135deg,#FFD700,#FF9500)',
            color:'white',fontSize:17,fontWeight:900,
            cursor:'pointer',fontFamily:"'Nunito',sans-serif",
            boxShadow:'0 6px 24px rgba(255,215,0,0.5)'}}>
          ✦ Choose Neevo — Let's Begin! ⭐
        </motion.button>
      </div>
    </motion.div>,

    // Step 2 — Name your pet
    <motion.div key="petname" initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} style={{textAlign:'center'}}>
      {selectedPet&&<motion.div animate={{y:[0,-10,0]}} transition={{repeat:Infinity,duration:2}} style={{margin:'0 auto 12px',display:'inline-block'}}>
        <img src="/neevo/Neevo_excited.png" alt="Neevo" style={{width:120,height:120,objectFit:'contain'}}/>
      </motion.div>}
      <h2 style={{fontSize:21,fontWeight:900,color:'white',margin:'0 0 6px'}}>Give them a name!</h2>
      <p style={{fontSize:13,color:'#888',fontWeight:700,margin:'0 0 16px'}}>Or keep the default name — they won't mind 💛</p>
      <input
        value={petNameInput}
        onChange={e=>setPetNameInput(e.target.value.slice(0,12))}
        placeholder="Neevo"
        style={{width:'100%',padding:'14px 16px',borderRadius:14,border:'2px solid #E0E8FF',
          outline:'none',fontSize:18,fontWeight:900,fontFamily:"'Nunito',sans-serif",
          color:'white',textAlign:'center',boxSizing:'border-box'}}/>
      <p style={{fontSize:11,color:'#BBB',fontWeight:700,marginTop:8}}>Max 12 characters</p>
      {selectedPet&&(
        <div style={{marginTop:12,background:'#F8F9FF',borderRadius:12,padding:'12px',textAlign:'left'}}>
          <p style={{margin:0,fontSize:13,fontWeight:700,color:'#555',lineHeight:1.6}}>
            <strong>{petNameInput||'Neevo'}</strong> will remember you, grow with you, and send you messages when you're away 💛
          </p>
        </div>
      )}
    </motion.div>,

    // Step 1 — Pick subjects
    <motion.div key="subjects" initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} style={{textAlign:'center'}}>
      <p style={{fontSize:36,margin:'0 0 8px'}}>🎯</p>
      <h2 style={{fontSize:21,fontWeight:900,color:'white',margin:'0 0 6px'}}>What do you love?</h2>
      <p style={{fontSize:13,color:'#888',fontWeight:700,margin:'0 0 16px'}}>Pick up to 3 — we'll show these first on your home screen</p>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
        {SUBJECT_OPTIONS.map(s=>{
          const sel=picked.includes(s.id);
          return(
            <button key={s.id} onClick={()=>toggle(s.id)} style={{padding:'12px 8px',borderRadius:12,border:`2px solid ${sel?'#FF6B00':'#E8E8E8'}`,background:sel?'linear-gradient(135deg,#FFF3E0,#FFF8E8)':'white',cursor:'pointer',fontFamily:"'Nunito',sans-serif",display:'flex',alignItems:'center',gap:8,boxShadow:sel?'0 2px 12px rgba(255,107,0,0.2)':'none',transition:'all 0.15s'}}>
              <span style={{fontSize:20}}>{s.emoji}</span>
              <span style={{fontSize:12,fontWeight:900,color:sel?'#FF6B00':'#444'}}>{s.label}</span>
              {sel&&<span style={{marginLeft:'auto',fontSize:13,color:'#FF6B00'}}>✓</span>}
            </button>
          );
        })}
      </div>
      {picked.length===0&&<p style={{fontSize:12,color:'#BBB',fontWeight:700,marginTop:10}}>Pick at least 1 to continue</p>}
    </motion.div>,

    // Step 2 — How it works
    <motion.div key="howto" initial={{opacity:0,x:40}} animate={{opacity:1,x:0}} style={{textAlign:'center'}}>
      <p style={{fontSize:36,margin:'0 0 8px'}}>🗺️</p>
      <h2 style={{fontSize:21,fontWeight:900,color:'white',margin:'0 0 6px'}}>How it works</h2>
      <p style={{fontSize:13,color:'#888',fontWeight:700,margin:'0 0 16px'}}>Learn this once — master it forever</p>
      <div style={{display:'flex',flexDirection:'column',gap:8,textAlign:'left'}}>
        {[
          {n:'1',icon:'📚',title:'Pick a World',desc:'Choose any subject from your home screen'},
          {n:'2',icon:'❓',title:'Answer Questions',desc:'Each correct answer earns XP + NeevCoins'},
          {n:'3',icon:'🔥',title:'Keep Your Streak',desc:'Log in daily — miss a day and it resets'},
          {n:'4',icon:'🎁',title:'Open Reward Chests',desc:'Hit 3, 7, 14 or 30-day streaks to unlock chests'},
          {n:'5',icon:'🏙️',title:'Build Your City',desc:'XP unlocks buildings in your personal city'},
        ].map(({n,icon,title,desc})=>(
          <div key={n} style={{display:'flex',alignItems:'flex-start',gap:12,background:'#F8F9FF',borderRadius:12,padding:'10px 12px'}}>
            <div style={{width:26,height:26,borderRadius:'50%',flexShrink:0,background:'linear-gradient(135deg,#00695C,#1A1A6E)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:900,color:'#1A1A2E'}}>{n}</div>
            <div>
              <p style={{margin:0,fontSize:13,fontWeight:900,color:'#1A1A2E'}}>{icon} {title}</p>
              <p style={{margin:'2px 0 0',fontSize:11,fontWeight:700,color:'#888'}}>{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </motion.div>,

    // Step 3 — Claim XP
    <motion.div key="mission" initial={{opacity:0,scale:0.9}} animate={{opacity:1,scale:1}} style={{textAlign:'center'}}>
      <motion.div animate={{scale:[1,1.08,1],rotate:[0,3,-3,0]}} transition={{duration:1.2,repeat:Infinity,repeatDelay:1}} style={{fontSize:64,margin:'0 0 12px',display:'block'}}>🎁</motion.div>
      <h2 style={{fontSize:23,fontWeight:900,color:'white',margin:'0 0 8px'}}>Your Welcome Gift!</h2>
      <div style={{background:'linear-gradient(135deg,#FFF9E6,#FFF3CD)',border:'2px solid #FFD600',borderRadius:16,padding:'16px',margin:'0 0 16px'}}>
        <p style={{fontSize:30,fontWeight:900,color:'#FF9500',margin:'0 0 4px'}}>+50 XP 🌟</p>
        <p style={{fontSize:14,fontWeight:700,color:'#795548',margin:0}}>Just for starting your journey!</p>
      </div>
      <p style={{fontSize:14,color:'#666',fontWeight:700,lineHeight:1.6,margin:'0 0 4px'}}>
        Claim your bonus and jump straight into <strong>{picked.length>0?(SUBJECT_OPTIONS.find(s=>s.id===picked[0])?.label||'your first world'):'your first world'}</strong>!
      </p>
      <p style={{fontSize:12,color:'#AAA',fontWeight:700,margin:'0 0 16px'}}>Answer 5 questions to unlock your first city building 🏙️</p>
      {claimed&&<motion.div initial={{scale:0}} animate={{scale:1}} style={{background:'#E8FFF0',border:'2px solid #00C851',borderRadius:12,padding:'12px',fontSize:15,fontWeight:900,color:'#00C851'}}>✅ +50 XP added! Loading your world...</motion.div>}
    </motion.div>,
  ];

  return(
    <div style={{position:'fixed',inset:0,background:'rgba(10,10,30,0.92)',zIndex:9999,display:'flex',alignItems:'flex-end',justifyContent:'center'}}>
      <motion.div initial={{y:400,opacity:0}} animate={{y:0,opacity:1}} transition={{type:'spring',stiffness:280,damping:28}}
        style={{
          background: step===1 ? 'transparent' : 'white',
          borderRadius:'28px 28px 0 0',
          padding: step===1 ? '0' : '28px 20px 40px',
          width:'100%',maxWidth:430,maxHeight:'96vh',overflowY:'auto',
          fontFamily:"'Nunito',sans-serif"
        }}>
        {/* Progress dots — hide on character step */}
        {step!==1&&(
          <div style={{display:'flex',justifyContent:'center',gap:6,marginBottom:22}}>
            {STEPS.map((_,i)=><div key={i} style={{height:4,borderRadius:2,transition:'all 0.3s',width:i===step?32:16,background:i<=step?'#FF6B00':'#E8E8E8'}}/>)}
          </div>
        )}
        <AnimatePresence mode="wait">{STEPS[step]}</AnimatePresence>
        {/* Hide bottom buttons on step 1 — it has its own CTA */}
        {step!==1&&<div style={{marginTop:22}}>
          {step<STEPS.length-1?(
            <motion.button whileTap={{scale:0.97}} disabled={!canNext} onClick={()=>setStep(s=>s+1)}
              style={{width:'100%',padding:'15px',borderRadius:16,border:'none',background:canNext?'linear-gradient(135deg,#0D1B4B,#7B5EA7)':'#E8E8E8',color:canNext?'white':'#AAA',fontSize:16,fontWeight:900,cursor:canNext?'pointer':'not-allowed',fontFamily:"'Nunito',sans-serif",boxShadow:canNext?'0 4px 20px rgba(123,94,167,0.35)':'none'}}>
              {step===0?`Meet Neevo! →`:step===2?`${petNameInput||'Neevo'} and I are ready! →`:step===3?'Got it! →':'Next →'}
            </motion.button>
          ):(
            <motion.button whileTap={{scale:0.97}} onClick={claimAndGo} disabled={claimed}
              style={{width:'100%',padding:'16px',borderRadius:16,border:'none',background:claimed?'#E8FFF0':'linear-gradient(135deg,#FFD600,#FF9500)',color:claimed?'#00C851':'white',fontSize:17,fontWeight:900,cursor:claimed?'default':'pointer',fontFamily:"'Nunito',sans-serif",boxShadow:claimed?'none':'0 4px 20px rgba(255,150,0,0.4)'}}>
              {claimed?'🚀 Loading your world...':'🎁 Claim +50 XP & Start!'}
            </motion.button>
          )}
          {step>0&&step<STEPS.length-1&&<button onClick={()=>onDone([])} style={{display:'block',width:'100%',background:'none',border:'none',color:'#BBB',fontWeight:700,fontSize:12,cursor:'pointer',marginTop:10,fontFamily:"'Nunito',sans-serif"}}>Skip for now</button>}
        </div>}
      </motion.div>
    </div>
  );
}

// ── PREMIUM UPGRADE MODAL ────────────────────────────────────
export function PremiumModal({onClose,onUpgrade,loading}){
  return(
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:9999,
      display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
      <motion.div initial={{scale:0.85,opacity:0}} animate={{scale:1,opacity:1}}
        transition={{type:'spring',stiffness:300}}
        style={{background:'white',borderRadius:'28px',padding:'24px 20px',maxWidth:'90vw',
          width:'100%',textAlign:'center',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
        <div style={{fontSize:'56px',marginBottom:'12px'}}>🌟</div>
        <h2 style={{fontSize:'22px',fontWeight:'900',color:'white',margin:'0 0 8px'}}>
          You've completed Level 1!
        </h2>
        <p style={{fontSize:'15px',color:'#666',fontWeight:'700',margin:'0 0 20px',lineHeight:'1.5'}}>
          Unlock <strong>all levels, unlimited questions, Mock Tests, Certificates</strong> and more!
        </p>
        <div style={{background:T.lYellow,borderRadius:'16px',padding:'16px',marginBottom:'20px'}}>
          <p style={{fontSize:'28px',fontWeight:'900',color:T.orange,margin:'0 0 2px'}}>₹1,000/year</p>
          <p style={{fontSize:'13px',color:'#888',fontWeight:'700',margin:0}}>Less than ₹3 per day!</p>
        </div>
        <div style={{textAlign:'left',marginBottom:'20px'}}>
          {['Unlimited questions in all verticals','10 AI Tutor questions/day','Unlimited Mock Tests',
            'Downloadable Certificate','Participate in Leaderboard'].map((f,i)=>(
            <div key={i} style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'8px'}}>
              <span style={{color:T.green,fontWeight:'900',fontSize:'16px'}}>✓</span>
              <span style={{fontSize:'14px',fontWeight:'700',color:'#1A1A2E'}}>{f}</span>
            </div>
          ))}
        </div>
        <motion.button whileTap={{scale:0.97}}
          style={{width:'100%',padding:'16px',borderRadius:'16px',border:'none',
            background:T.grad2,color:'white',fontSize:'17px',fontWeight:'900',
            cursor:'pointer',fontFamily:"'Nunito',sans-serif",marginBottom:'10px'}}
          onClick={onUpgrade} disabled={loading}>
          {loading?'Processing...':'Go Premium 🌟 — ₹1,000/yr'}
        </motion.button>
        <button style={{background:'none',border:'none',color:'#AAA',fontSize:'14px',
          fontWeight:'700',cursor:'pointer',fontFamily:"'Nunito',sans-serif"}}
          onClick={onClose}>Maybe later</button>
      </motion.div>
    </div>
  );
}

// ── HOME SCREEN ──────────────────────────────────────────────
export default function HomeScreen({onNavigate,profile,xp,streak,isPremium,onUpgrade,onDailyChallenge,completedCounts,onXPEarned,coins,onCoinsEarned,onOpenSpin,onOpenShop,missionRefreshTick=0,onOpenChat,user,todayXPEarned=0}){
  const level=Math.floor(xp/200)+1;
  const xpInLevel=xp%200;
  const todayChallenge=DAILY_CHALLENGES[new Date().getDay()];
  const levelNames=['Curious Cub','Smart Starter','Market Explorer','Deal Maker','Brand Builder',
    'Team Leader','Digital Pioneer','Impact Maker','Scale Champion','Young CEO'];
  const todayKey=`ns_daily_${profile?.parent_email}_${new Date().toDateString()}`;
  const dailyDone=!!localStorage.getItem(todayKey);
  const [searchQuery,setSearchQuery]=useState('');
  const [searchFocused,setSearchFocused]=useState(false);
  const [assignments,setAssignments]=useState([]);

  // Daily XP Goal
  const GOALS=[20,50,100];
  const goalKey=`ns_xp_goal_${profile?.parent_email}`;
  const [dailyGoal,setDailyGoal]=useState(()=>parseInt(localStorage.getItem(goalKey)||'20',10));
  const [showGoalPicker,setShowGoalPicker]=useState(false);
  const [goalCelebrated,setGoalCelebrated]=useState(()=>!!localStorage.getItem(`ns_goal_hit_${profile?.parent_email}_${new Date().toDateString()}`));
  const goalProgress=Math.min(todayXPEarned,dailyGoal);
  const goalPct=Math.min(100,Math.round((goalProgress/dailyGoal)*100));
  const goalHit=todayXPEarned>=dailyGoal;

  useEffect(()=>{
    if(goalHit&&!goalCelebrated){
      setGoalCelebrated(true);
      localStorage.setItem(`ns_goal_hit_${profile?.parent_email}_${new Date().toDateString()}`,'1');
      setTimeout(()=>{playSound('levelup');fireConfetti();},300);
    }
  },[goalHit,goalCelebrated,profile?.parent_email]);

  useEffect(()=>{
    if(!profile?.school_code)return;
    const today=new Date().toISOString().split('T')[0];
    supabase.from('ns_assignments').select('*')
      .eq('school_code',profile.school_code)
      .eq('class_level',profile.class_level)
      .gte('due_date',today)
      .order('due_date',{ascending:true})
      .then(async({data:asgn})=>{
        if(!asgn||asgn.length===0){setAssignments([]);return;}
        const{data:done}=await supabase.from('ns_assignment_completions')
          .select('assignment_id').eq('student_email',profile.parent_email);
        const doneIds=new Set((done||[]).map(d=>d.assignment_id));
        setAssignments(asgn.filter(a=>!doneIds.has(a.id)));
      }).catch(()=>{setAssignments([]);});
  },[profile?.school_code,profile?.class_level,profile?.parent_email]);// eslint-disable-line react-hooks/exhaustive-deps

  const markAssignmentDone=async(a)=>{
    await supabase.from('ns_assignment_completions')
      .insert({assignment_id:a.id,student_email:profile.parent_email});
    if(onXPEarned)onXPEarned(a.xp_reward);
    playSound('correct');
    fireConfetti();
    setAssignments(prev=>prev.filter(x=>x.id!==a.id));
  };

  const ALL_ITEMS=[
    {id:'subject_practice',icon:'📖',title:'CBSE Practice',tags:'cbse subject practice maths science english chapter class curriculum ncert'},
    {id:'olympiad',icon:'🏆',title:'Olympiad Prep',tags:'olympiad sof imo nso ieo igko ico maths science english gk computers exam'},
    {id:'mocktest',icon:'📝',title:'Mock Test',tags:'mock test olympiad exam practice sof 20 questions'},
    {id:'brain',icon:'🧠',title:'Brain Buzz',tags:'brain buzz quiz riddles logic numbers india gk science trivia'},
    {id:'quiz',icon:'🎯',title:'Quick Quiz',tags:'quiz gk general knowledge questions class'},
    {id:'maths',icon:'➗',title:'Maths Games',tags:'maths games speed maths number puzzle story problem tricks arithmetic'},
    {id:'stem',icon:'🔬',title:'STEM Lab',tags:'stem science experiments chemistry physics biology lab'},
    {id:'science',icon:'⚗️',title:'Science World',tags:'science chemistry physics biology lab experiments'},
    {id:'entrepreneurship',icon:'🏪',title:'Biz World',tags:'biz world business entrepreneur scenario startup money'},
    {id:'money',icon:'💰',title:'Money School',tags:'money finance savings investment budget bank interest tax stocks'},
    {id:'ai_tutor',icon:'🤖',title:'AI School',tags:'ai artificial intelligence machine learning robot internet coding blockchain'},
    {id:'language',icon:'🗣️',title:'Language World',tags:'language english hindi tamil vocabulary words meaning'},
    {id:'stories',icon:'⭐',title:'Role Model Stories',tags:'stories role models inspiration ratan tata kalpana chawla apj kalam sundar pichai'},
    {id:'challenges',icon:'💡',title:'Idea Lab',tags:'idea lab challenges business weekly writing innovation creativity'},
    {id:'news',icon:'📰',title:'News for Kids',tags:'news current affairs india space science technology sports'},
    {id:'games',icon:'🎮',title:'Games Zone',tags:'games word scramble true false quick maths fun play'},
    {id:'spotit',icon:'👀',title:'Spot the Diff',tags:'spot difference find visual puzzle scene'},
    {id:'homework',icon:'📚',title:'Homework Helper',tags:'homework help ai tutor assistant questions answers'},
    {id:'leaderboard',icon:'🏅',title:'Leaderboard',tags:'leaderboard rank top students score xp'},
    {id:'certificate',icon:'🎓',title:'Certificate',tags:'certificate achievement reward completion'},
    {id:'profile',icon:'👤',title:'My Profile',tags:'profile xp level streak badges progress account'},
  ];

  const searchResults=searchQuery.trim().length>=2
    ? ALL_ITEMS.filter(item=>{
        const q=searchQuery.toLowerCase();
        return item.title.toLowerCase().includes(q)||item.tags.includes(q);
      }).slice(0,6)
    : [];

  return(
    <div style={S.screen} className="ns-screen">
      {/* Header — hidden on desktop (sidebar shows user info) */}
      <div style={S.header} className="ns-home-header">
        <div style={S.headerTop}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <div onClick={onOpenChat} style={{cursor:'pointer',display:'flex',flexDirection:'column',alignItems:'center',gap:1}} title="Chat with NeevBot">
              <NeevBotMini coins={coins||0}/>
              <span style={{fontSize:10,fontWeight:900,color:'rgba(255,255,255,0.6)',letterSpacing:'0.03em'}}>CHAT</span>
            </div>
            <h1 style={S.appName}>🌟 NeevVerse</h1>
          </div>
          <div style={{display:'flex',gap:'6px',alignItems:'center'}}>
            <div onClick={onOpenShop} style={{cursor:'pointer'}} title="NeevCoins Shop">
              <NeevCoinsBar coins={coins||0}/>
            </div>
            <div style={{background:'rgba(255,255,255,0.2)',borderRadius:'16px',padding:'4px 10px',fontSize:'12px',fontWeight:'800',color:'#1A1A2E'}}>🔥 {streak}d</div>
            {assignments.length>0&&(
              <div onClick={()=>document.getElementById('ns-assignments-card')?.scrollIntoView({behavior:'smooth'})}
                style={{background:'#FF2D55',borderRadius:'16px',padding:'4px 10px',
                  fontSize:'11px',fontWeight:'900',color:'white',cursor:'pointer',
                  display:'flex',alignItems:'center',gap:4}}>
                🔔 {assignments.length}
              </div>
            )}
          </div>
        </div>
        <div style={S.greetCard}>
          <span style={{fontSize:'38px'}}>⭐</span>
          <div style={{flex:1}}>
            <p style={S.gTitle}>Hey {profile?.child_name||'Champ'}! 👋</p>
            <p style={S.gSub}>{levelNames[Math.min(level-1,9)]} • Ready to learn? 🚀</p>
          </div>
          <div style={{textAlign:'center'}}>
            <div style={{fontSize:'22px',fontWeight:'900',color:T.yellow}}>{xp}</div>
            <div style={{fontSize:'10px',color:'rgba(255,255,255,0.8)',fontWeight:'700'}}>XP</div>
          </div>
        </div>
        <div style={S.xpRow}>
          <span style={S.xpLabel}>Level {level}</span>
          <div style={S.xpTrack}>
            <motion.div style={{...S.xpFill,width:`${xpInLevel/2}%`}}
              initial={{width:0}} animate={{width:`${xpInLevel/2}%`}} transition={{duration:1}}/>
          </div>
          <span style={S.xpVal}>{200-xpInLevel} XP to next</span>
        </div>
      </div>

      {/* Desktop-only greeting bar (visible only on large screens via CSS) */}
      <div className="ns-desktop-topbar" style={{display:'none'}}>
        <div style={{background:T.gradHeader,padding:'20px 24px 24px',borderRadius:'0 0 24px 24px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
            <div>
              <h2 style={{color:'white',margin:'0 0 4px',fontFamily:"'Nunito',sans-serif",fontSize:22,fontWeight:900}}>
                Hey {profile?.child_name||'Champ'}! 👋 Ready to learn?
              </h2>
              <p style={{color:'rgba(255,255,255,0.8)',margin:0,fontSize:14,fontWeight:700,fontFamily:"'Nunito',sans-serif"}}>
                {levelNames[Math.min(level-1,9)]} · {xp} XP total
              </p>
            </div>
            <div style={{display:'flex',gap:8}}>
              {DAILY_CHALLENGES[new Date().getDay()]&&(
                <div style={{background:'rgba(255,255,255,0.15)',borderRadius:14,padding:'8px 14px',cursor:'pointer'}}
                  onClick={()=>onNavigate(DAILY_CHALLENGES[new Date().getDay()].screen)}>
                  <div style={{fontSize:10,color:'#555',fontWeight:800,fontFamily:"'Nunito',sans-serif"}}>TODAY'S CHALLENGE</div>
                  <div style={{fontSize:14,color:'white',fontWeight:900,fontFamily:"'Nunito',sans-serif"}}>
                    {DAILY_CHALLENGES[new Date().getDay()].title}
                    <span style={{marginLeft:8,background:'rgba(255,214,0,0.3)',borderRadius:8,padding:'1px 7px',fontSize:11}}>{DAILY_CHALLENGES[new Date().getDay()].bonus}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div style={{padding:'12px 16px 0',position:'relative'}}>
        <div style={{display:'flex',alignItems:'center',gap:'10px',background:searchFocused?'white':'#F5F5F5',
          borderRadius:'16px',padding:'10px 14px',border:`2px solid ${searchFocused?T.orange:'transparent'}`,
          transition:'all 0.2s'}}>
          <span style={{fontSize:'18px'}}>🔍</span>
          <input
            value={searchQuery}
            onChange={e=>setSearchQuery(e.target.value)}
            onFocus={()=>setSearchFocused(true)}
            onBlur={()=>setTimeout(()=>setSearchFocused(false),150)}
            placeholder="Search subjects, topics, games..."
            style={{flex:1,border:'none',background:'transparent',fontSize:'14px',fontWeight:'700',
              fontFamily:"'Nunito',sans-serif",outline:'none',color:'#1A1A2E'}}/>
          {searchQuery&&(
            <button onClick={()=>setSearchQuery('')}
              style={{border:'none',background:'none',cursor:'pointer',fontSize:'16px',color:'#AAA',padding:0}}>✕</button>
          )}
        </div>
        {/* Search Results Dropdown */}
        {searchResults.length>0&&(
          <motion.div initial={{opacity:0,y:-10}} animate={{opacity:1,y:0}}
            style={{position:'absolute',top:'100%',left:'16px',right:'16px',background:'white',
              borderRadius:'16px',boxShadow:'0 8px 32px rgba(0,0,0,0.12)',zIndex:100,
              border:`1px solid #E8E8E8`,overflow:'hidden',marginTop:'4px'}}>
            {searchResults.map((item,i)=>(
              <div key={item.id} onClick={()=>{onNavigate(item.id);setSearchQuery('');}}
                style={{display:'flex',alignItems:'center',gap:'12px',padding:'12px 16px',
                  cursor:'pointer',borderBottom:i<searchResults.length-1?'1px solid #F5F5F5':'none',
                  background:'white'}}
                onMouseEnter={e=>e.currentTarget.style.background='#F8F9FF'}
                onMouseLeave={e=>e.currentTarget.style.background='white'}>
                <span style={{fontSize:'22px'}}>{item.icon}</span>
                <p style={{fontSize:'14px',fontWeight:'800',color:'white',margin:0}}>{item.title}</p>
              </div>
            ))}
          </motion.div>
        )}
        {searchQuery.trim().length>=2&&searchResults.length===0&&(
          <motion.div initial={{opacity:0}} animate={{opacity:1}}
            style={{position:'absolute',top:'100%',left:'16px',right:'16px',background:'white',
              borderRadius:'16px',boxShadow:'0 8px 32px rgba(0,0,0,0.12)',zIndex:100,
              padding:'16px',textAlign:'center',marginTop:'4px'}}>
            <p style={{fontSize:'22px',margin:'0 0 6px'}}>🔍</p>
            <p style={{fontSize:'14px',fontWeight:'800',color:'#555',margin:'0 0 2px'}}>Hmm, nothing found!</p>
            <p style={{fontSize:'12px',color:'#AAA',fontWeight:'700',margin:0}}>Try "maths", "quiz", or "stories" — your pet recommends stories 📖</p>
          </motion.div>
        )}
      </div>

      {/* New user welcome banner */}
      {xp===0&&(
        <motion.div style={{background:T.grad3,borderRadius:'20px',padding:'16px',
          margin:'16px 16px 0',display:'flex',alignItems:'center',gap:'12px'}}
          initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
          transition={{delay:0.2}}>
          <span style={{fontSize:'32px'}}>👋</span>
          <div>
            <p style={{fontSize:'14px',fontWeight:'900',color:'white',margin:'0 0 2px'}}>
              Welcome to NeevStar!
            </p>
            <p style={{fontSize:'12px',color:'rgba(255,255,255,0.9)',margin:0,fontWeight:'700'}}>
              Start with Biz World or Quiz Zone to earn your first XP! 🌟
            </p>
          </div>
        </motion.div>
      )}

      {/* Daily Challenge Card — hidden on desktop (shown in topbar instead) */}
      {/* Assignments Card — only if school student has pending assignments */}
      {assignments.length>0&&(
        <div id="ns-assignments-card" style={{padding:'12px 16px 0'}}>
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.2}}>
            <div style={{background:'white',borderRadius:20,padding:'14px 16px',boxShadow:'0 2px 12px rgba(0,0,0,0.07)',border:'2px solid #E8F0FF'}}>
              <p style={{fontSize:11,fontWeight:900,color:T.blue,margin:'0 0 10px',letterSpacing:'0.08em'}}>📋 YOUR ASSIGNMENTS</p>
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                {assignments.slice(0,3).map(a=>(
                  <div key={a.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,background:'#F8F9FF',borderRadius:12,padding:'10px 12px'}}>
                    <div style={{flex:1}}>
                      <p style={{margin:0,fontSize:13,fontWeight:900,color:'#1A1A2E'}}>{a.title}</p>
                      <p style={{margin:0,fontSize:11,color:'#888',fontWeight:700}}>{a.subject} • Due {new Date(a.due_date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</p>
                    </div>
                    <span style={{background:T.lOrange,color:T.orange,borderRadius:8,padding:'3px 8px',fontSize:11,fontWeight:900,flexShrink:0}}>⚡{a.xp_reward} XP</span>
                    <button onClick={()=>markAssignmentDone(a)}
                      style={{background:T.green,border:'none',color:'white',borderRadius:10,padding:'6px 12px',
                        cursor:'pointer',fontFamily:"'Nunito',sans-serif",fontWeight:800,fontSize:12,flexShrink:0}}>
                      ✓ Done
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Jump Back In */}
      {(()=>{
        const lastScreen=profile?.parent_email?localStorage.getItem(`ns_last_screen_${profile.parent_email}`):null;
        if(!lastScreen||lastScreen==='home') return null;
        const WORLD_LABELS={'quiz':'Quiz World','olympiad':'Olympiad Prep','maths':'Maths Temple',
          'science':'Science & STEM','stem':'Science & STEM','money':'Money City',
          'entrepreneurship':'Startup Garage','ai_tutor':'AI Lab','language':'Language Kingdom',
          'stories':'Hall of Legends','news':'News Tower','challenges':'Innovation Island',
          'games':'Game Zone','creator_studio':'Creator Studio','brain':'Quiz World',
          'subject_practice':'CBSE Practice','mocktest':'Test Colosseum','homework':'Homework HQ'};
        const WORLD_ICONS={'quiz':'🏆','olympiad':'🎓','maths':'➗','science':'🔬','stem':'⚗️',
          'money':'💰','entrepreneurship':'🏗️','ai_tutor':'🤖','language':'🦜',
          'stories':'⭐','news':'📰','challenges':'💡','games':'🎮',
          'creator_studio':'🎬','brain':'🧩','subject_practice':'📖','mocktest':'📝','homework':'📚'};
        const label=WORLD_LABELS[lastScreen]||lastScreen;
        const icon=WORLD_ICONS[lastScreen]||'📚';
        return(
          <div style={{padding:'12px 16px 0'}}>
            <motion.div initial={{opacity:0,x:-20}} animate={{opacity:1,x:0}} transition={{delay:0.15}}
              onClick={()=>onNavigate(lastScreen)}
              style={{background:'white',borderRadius:18,padding:'14px 16px',
                boxShadow:'0 2px 12px rgba(0,0,0,0.08)',cursor:'pointer',
                display:'flex',alignItems:'center',gap:12,
                border:'2px solid #E8E8F0'}}>
              <div style={{width:48,height:48,borderRadius:14,
                background:'linear-gradient(135deg,#00695C,#1A1A6E)',
                display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,flexShrink:0}}>
                {icon}
              </div>
              <div style={{flex:1,minWidth:0}}>
                <p style={{margin:'0 0 2px',fontSize:12,fontWeight:900,color:'#888',letterSpacing:'0.04em'}}>
                  ▶ JUMP BACK IN
                </p>
                <p style={{margin:0,fontSize:15,fontWeight:900,color:'white',
                  whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                  {label}
                </p>
              </div>
              <div style={{background:'linear-gradient(135deg,#00695C,#1A1A6E)',
                borderRadius:10,padding:'8px 14px',flexShrink:0}}>
                <span style={{fontSize:13,fontWeight:900,color:'#1A1A2E'}}>Go →</span>
              </div>
            </motion.div>
          </div>
        );
      })()}

      {/* Daily XP Goal */}
      <div style={{padding:'12px 16px 0'}}>
        <motion.div initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} transition={{delay:0.18}}
          style={{background:goalHit?'linear-gradient(135deg,#00C851,#00E676)':'linear-gradient(135deg,#1A1A2E,#2A1A4E)',
            borderRadius:18,padding:'14px 16px',cursor:'pointer'}}
          onClick={()=>setShowGoalPicker(v=>!v)}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:20}}>{goalHit?'🎉':'🎯'}</span>
              <div>
                <p style={{margin:0,fontSize:13,fontWeight:900,color:'#1A1A2E'}}>
                  {goalHit?'Daily Goal Crushed! 🔥':`Daily XP Goal`}
                </p>
                <p style={{margin:0,fontSize:11,color:'rgba(255,255,255,0.6)',fontWeight:700}}>
                  {goalHit?`+${dailyGoal} XP earned today!`:`${goalProgress} / ${dailyGoal} XP today`}
                </p>
              </div>
            </div>
            <span style={{fontSize:12,fontWeight:900,color:'rgba(255,255,255,0.5)'}}>
              {goalHit?'✓':goalPct+'%'}
            </span>
          </div>
          <div style={{background:'rgba(255,255,255,0.15)',borderRadius:8,height:8,overflow:'hidden'}}>
            <motion.div initial={{width:0}} animate={{width:`${goalPct}%`}} transition={{duration:0.8}}
              style={{height:'100%',borderRadius:8,
                background:goalHit?'rgba(255,255,255,0.8)':'linear-gradient(90deg,#FFD600,#FF9500)'}}/>
          </div>
        </motion.div>
        {/* Goal picker */}
        <AnimatePresence>
          {showGoalPicker&&(
            <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}}
              style={{background:'white',borderRadius:14,padding:'14px',marginTop:6,
                boxShadow:'0 4px 20px rgba(0,0,0,0.15)'}}>
              <p style={{margin:'0 0 10px',fontSize:13,fontWeight:900,color:'#1A1A2E'}}>Choose your daily goal:</p>
              <div style={{display:'flex',gap:8}}>
                {GOALS.map(g=>(
                  <button key={g} onClick={()=>{
                    setDailyGoal(g);
                    localStorage.setItem(goalKey,String(g));
                    setShowGoalPicker(false);
                  }} style={{flex:1,padding:'12px 8px',borderRadius:12,border:`2px solid ${g===dailyGoal?T.blue:'#E8E8E8'}`,
                    background:g===dailyGoal?T.lBlue:'white',cursor:'pointer',
                    fontFamily:"'Nunito',sans-serif",fontWeight:900,fontSize:14,
                    color:g===dailyGoal?T.blue:'#888'}}>
                    {g} XP
                    <p style={{margin:'2px 0 0',fontSize:10,fontWeight:700,color:'#AAA'}}>
                      {g===20?'Casual':g===50?'Regular':'Champion'}
                    </p>
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mystery Mission */}
      <div style={{padding:'12px 16px 0'}}>
        <motion.div initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} transition={{delay:0.22}}>
          <MysteryMission
            profile={profile}
            onClaim={(c)=>onCoinsEarned&&onCoinsEarned(c)}
            refreshTrigger={missionRefreshTick}
          />
        </motion.div>
      </div>

      <div className="ns-daily-card" style={{padding:'12px 16px 0'}}>
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.25}}
          onClick={()=>{
            if(!dailyDone&&onDailyChallenge){
              const bonus=parseInt((todayChallenge.bonus||'+20 Bonus XP').replace(/[^0-9]/g,'')||'20',10);
              onDailyChallenge(todayChallenge.screen,bonus);
            } else {
              onNavigate(todayChallenge.screen);
            }
          }}
          style={{background:dailyDone?'linear-gradient(135deg,#00C851,#00E676)':`linear-gradient(135deg,${todayChallenge.color},${todayChallenge.color}CC)`,
            borderRadius:20,padding:'14px 16px',color:'white',cursor:'pointer',
            display:'flex',alignItems:'center',gap:12,
            boxShadow:dailyDone?'0 4px 20px rgba(0,200,81,0.4)':`0 4px 20px ${todayChallenge.color}44`}}>
          <div style={{fontSize:36,flexShrink:0}}>{dailyDone?'✅':todayChallenge.icon}</div>
          <div style={{flex:1}}>
            <p style={{fontSize:10,fontWeight:900,opacity:0.85,margin:'0 0 2px',letterSpacing:'0.08em'}}>TODAY'S CHALLENGE</p>
            <p style={{fontSize:15,fontWeight:900,margin:'0 0 2px'}}>{todayChallenge.title}</p>
            <p style={{fontSize:12,opacity:0.9,margin:0,fontWeight:700}}>{dailyDone?'Completed! Great work! 🎉':todayChallenge.desc}</p>
          </div>
          <div style={{textAlign:'center',flexShrink:0}}>
            <div style={{background:'rgba(255,255,255,0.25)',borderRadius:12,padding:'6px 10px'}}>
              <p style={{fontSize:11,fontWeight:900,margin:0}}>{dailyDone?'Done ✓':todayChallenge.bonus}</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Progress Tracker Card — compact */}
      <div style={{padding:'12px 16px 0'}}>
        <motion.div style={{background:`linear-gradient(135deg,${T.purple},${T.pink})`,borderRadius:'18px',padding:'12px 16px',color:'white',display:'flex',alignItems:'center',gap:12}}
          initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.3}}>
          <div style={{fontSize:'28px'}}>🏆</div>
          <div style={{flex:1}}>
            <p style={{margin:0,fontSize:'15px',fontWeight:'900'}}>Level {level} — {levelNames[Math.min(level-1,9)]}</p>
            <div style={{background:'rgba(255,255,255,0.25)',borderRadius:'6px',height:'5px',marginTop:6,overflow:'hidden'}}>
              <motion.div initial={{width:0}} animate={{width:`${(xp%200)/2}%`}} transition={{duration:1,delay:0.4}}
                style={{height:'100%',background:'rgba(255,255,255,0.9)',borderRadius:'6px'}}/>
            </div>
          </div>
          <div style={{textAlign:'right',flexShrink:0}}>
            <p style={{margin:0,fontSize:'15px',fontWeight:'900',color:T.yellow}}>{xp}</p>
            <p style={{margin:0,fontSize:'10px',opacity:0.8,fontWeight:'700'}}>{200-(xp%200)} to next</p>
          </div>
        </motion.div>
      </div>

      {/* NeevPet Widget */}
      <AchievementTicker userId={user?.id}/>
      <NeevPetWidget xp={xp} streak={streak} coins={coins} onOpen={()=>onNavigate('neevpet')}/>
      <CardStrip onViewAll={()=>onNavigate('cards')}/>

      {/* NeevVerse City Map */}
      <CityMap onNavigate={onNavigate} profile={profile} user={user} />

      {/* Floating Lucky Spin button */}
      <motion.button
        onClick={onOpenSpin}
        whileTap={{scale:0.92}}
        animate={{y:[0,-6,0]}}
        transition={{duration:2,repeat:Infinity,ease:'easeInOut'}}
        className="ns-spin-btn"
        style={{
          position:'fixed',bottom:88,right:16,zIndex:900,
          width:58,height:58,borderRadius:'50%',
          background:'linear-gradient(135deg,#FFD600,#FF9500)',
          border:'none',cursor:'pointer',
          boxShadow:'0 4px 20px rgba(255,150,0,0.5)',
          display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
          fontSize:22,gap:0,
        }}>
        🎰
        <span style={{fontSize:10,fontWeight:900,color:'white',letterSpacing:'0.03em',lineHeight:1.2}}>SPIN</span>
      </motion.button>
    </div>
  );
}

